import * as THREE from 'three';
import type { Vec3, Waypoint } from '@/types';

/**
 * Central tunable configuration. Every "magic number" in the experience lives here
 * so subsystems stay declarative and a single file drives the whole pipeline.
 */
export const settings = {
  time: {
    /** Clamp delta to avoid huge jumps after a tab switch / breakpoint. */
    maxDelta: 1 / 30,
  },

  renderer: {
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance' as WebGLPowerPreference,
    // ON (2026-07-20): the cursor glow is stencil-MASKED to the emissive twins' silhouette —
    // the twins' overlays stamp the stencil, the glow quad only draws where it's stamped, so it
    // can never brighten the baked stone texture around the emission. Needs a stencil buffer.
    stencil: true,
    /** Hard cap on devicePixelRatio (proven pipeline rule). */
    maxPixelRatio: 2,
    clearColor: '#cdd9e6',
    clearAlpha: 1,
    /** ACES Filmic per the user's request (2026-07-02) — pairs with the new Blender-baked
     *  stone/pillar/stage textures. (Was AgX for the golden-hour highlights; revisit if the
     *  baked textures clip under ACES' filmic shoulder.) */
    toneMapping: THREE.ACESFilmicToneMapping,
    toneMappingExposure: 1.0,
    outputColorSpace: THREE.SRGBColorSpace,
    shadows: {
      // OFF (2026-07-11, user: "everything baked … remove that sun and all"). The baked
      // atlases already contain their shadows; real-time shadows would double up and only
      // grass/water are lit dynamically now anyway.
      enabled: false,
      type: THREE.PCFSoftShadowMap,
      autoUpdate: true,
    },
  },

  camera: {
    fov: 37,
    near: 0.1,
    /**
     * MUST exceed (furthest the camera gets from the world origin) + (the `Sky` dome's radius),
     * because that dome is centred on the ORIGIN and does NOT follow the camera.
     *
     * Raised 1000 → 1600 (2026-07-17). At 1000 this was a real, visible bug: the baked camera
     * travels ~496 units down the path, so from ~frame 105 onward the far side of the 900-radius
     * dome sat beyond the far plane and got CLIPPED — punching a hole that showed the renderer's
     * `clearColor` (#cdd9e6) through it. It read as a pale, faceted white polygon floating in the
     * scene (user: "what is that white thing… i don't want that"). Worst case is frame 370:
     * 495.8 + 900 = 1395.8, so 1600 leaves headroom.
     *
     * NOTE: no smaller radius can fix this on its own — the dome must also CONTAIN the camera
     * (radius > 496), and 496 + radius < 1000 is impossible. So either `far` covers it (this) or
     * the dome has to follow the camera. Depth precision is dominated by `near` (0.1) anyway, so
     * 1000 → 1600 costs effectively nothing.
     */
    far: 1600,
    start: {
      position: [0.54, 3.69, 22.78] as Vec3,
      target: [0, 3, 0] as Vec3,
    },
    /** Per-frame smoothing for position lerp / quaternion slerp (lower = slower/more
     *  lag). Slowed down from 0.1 (2026-07-05, user: camera moved too fast/snappy on
     *  scroll — wanted a slower, more cinematic glide). */
    positionSmoothing: 0.045,
    rotationSmoothing: 0.045,
    /** Per-frame smoothing for FOV (focal-length) changes — deliberately gentler than the
     *  pose smoothing (2026-09-01, user: "when the focal length changes it's stuttering"): an
     *  exponential ease is FASTEST on its first frame, and at 20°+ of zoom that onset kick
     *  read as a shudder. Magnet glides now drive the zoom continuously themselves; this
     *  softens the remaining one-shot paths (reverse boundary crossings etc.). */
    fovSmoothing: 0.022,
    /**
     * MAX FOLLOW LAG (2026-09-01, the real "camera passes through meshes" fix). The smoothed
     * camera trails its path target by ~velocity × 0.37s; during a fast catch-up that lag grows
     * to several world units, and on a curved stretch (the doorway) the trailing camera CUTS THE
     * CORNER straight through the wall — the baked path itself never clips, only the laggy
     * follower does. This caps the camera's distance behind its target (world units): within the
     * cap the glide-smoothing is untouched, beyond it the camera is held on a tight tube around
     * the authored path, which is collision-free by construction. 0 = off.
     */
    maxFollowLag: 1.2,
    /** Per-frame smoothing (0..1, lower = laggier/smoother) applied to the SCRUB
     *  progress itself before it drives the baked camera — a GSAP ScrollTrigger
     *  "scrub: <number>"-style eased catch-up, so the camera glides with scroll
     *  instead of snapping 1:1 to every wheel tick. See {@link ScrollCameraTest}.
     *  Slowed from 0.08 alongside `positionSmoothing`/`rotationSmoothing` above. */
    scrollEase: 0.035,
    /**
     * GLOBAL CAMERA SPEED LIMIT (2026-09-01, user: "when I scroll fast the camera is moving too
     * fast — sometimes it just passes through some mesh, feels unnatural. I want it slow even if
     * someone is scrolling so fast"). The clip may NEVER advance faster than this many baked
     * frames per second, whatever the scroll velocity — a hard flick still arrives, the camera
     * just travels there at a composed pace, so it can never teleport through geometry. Applies
     * in both directions; the slow zones below can only tighten it further. 0 = off.
     */
    maxFramesPerSec: 24,
    /**
     * SCROLL-DRIVEN FOCAL RAMP (2026-09-01, with `scenes.magneticPulls` off): each section's
     * focal length is reached by a continuous log-focal smoothstep across this many clip frames
     * BEFORE its boundary — e.g. C1→C2's 35→20mm plays across frames 40→64 (the door approach)
     * and is exactly 20mm at frame 64. Frame-driven, so scrubbing back plays the zoom in
     * reverse identically. See `SceneSections.focalAtFrame`.
     */
    focalRampFrames: 24,
    /**
     * SLOW ZONES (2026-07-23, user: "in section 3 it's so fast on scroll — a little slowness
     * even when the user scrolls fast, not too much"): inside a zone the clip may never
     * advance faster than `maxFramesPerSec`, whatever the scroll velocity — the scrub still
     * follows the user's position, it just takes its time through those frames (both
     * directions). Magnet glides are unaffected (they drive the camera directly).
     *
     * 40→64 added 2026-09-01 (user: "the door animations open so quickly, feels not natural"):
     * the doors are baked across the opening frames, so capping the clip rate there IS the
     * door-opening speed — scrolling into Chapter 1's arch is now always an unhurried reveal.
     */
    slowZones: [
      { startFrame: 40, endFrame: 64, maxFramesPerSec: 12 },
      { startFrame: 128, endFrame: 190, maxFramesPerSec: 22 },
    ] as Array<{
      startFrame: number;
      endFrame: number;
      maxFramesPerSec: number;
    }>,
    mobileFovMultiplier: 1.18,
    /** Adopt the Blender camera baked in the GLB (its world transform + FOV). */
    useModelCamera: true,
    /** Override the Blender camera's lens with a focal length (mm, full-frame 36mm
     *  gauge). `null` keeps the GLB's exact FOV. Matches Scene 1's own focal length
     *  (`SceneSections.ts`) so the initial setup and the section system agree. */
    focalLength: 35 as number | null,
    /** Dolly the Blender camera straight back along its view axis (world units), to
     *  pull it off the arch. 0 keeps the exact GLB position. */
    dollyBack: 5,
    /** Technical scroll-camera test — off; the Blender camera is authoritative now. */
    scrollTest: {
      enabled: false,
      waypoints: [
        { position: [10, 4, 10], target: [0, 1, 0] },
        { position: [0, 6, 13], target: [0, 1.5, 0] },
        { position: [-11, 5, 7], target: [-2, 1, 0] },
        { position: [-6, 3, -11], target: [0, 1, -2] },
      ] as Waypoint[],
    },
    /** Subtle mouse-driven camera LOOK — rotates the camera a little toward wherever
     *  the pointer is (up/down/left/right), it never moves position. Drives
     *  `CameraController.parallax` (a dormant hook made exactly for this), so it
     *  composes with the baked/scroll camera rather than fighting it. See
     *  {@link MouseLook}. */
    mouseLook: {
      enabled: true,
      /** Max yaw (left/right) at the pointer's horizontal edge, degrees. */
      maxYawDeg: 4,
      /** Max pitch (up/down) at the pointer's vertical edge, degrees. */
      maxPitchDeg: 2.5,
      /** Per-frame follow smoothing (0..1, higher = snappier). */
      smoothing: 0.06,
    },
  },

  /** Distance fog — the camera-LOD trick for the 4-scene baked path: the scenes sit
   *  ~125-150 world units apart along Z (the camera travels Z≈+31 → Z≈-474 over the
   *  bake), so whichever scene isn't the current one yet should be hazed into the
   *  cloud sea rather than visible far down the path. LINEAR fog (not exponential —
   *  see `SceneManager`) so `near` gives an explicit clear radius right around the
   *  camera (2026-07-04, user: "near camera I don't want too much fog") and `far` is
   *  where it's fully hazed. Both live-tunable in the debug GUI. */
  fog: {
    // ON (2026-07-21, user: "enable fog, default clear radius 17 and fully hazed far 300").
    enabled: true,
    color: '#eadfca',
    /** Stay fully clear out to this distance from the camera. */
    near: 17,
    /** Fully hazed by this distance. */
    far: 300,
  },

  /**
   * CAMERA FOG ENVELOPE (2026-07-23, user: "at frame 0 the camera is covered with fog planes —
   * the user can't see anything — and they fade out smoothly by frame 40; at ~340 the clouds
   * cover the camera again so the loop restarts inside them"). Big soft `Cloud_Texture.png`
   * sprites parked in the camera's near field, opacity driven purely by the current clip frame
   * (see `CameraFog`). This is what makes the boot, the intro, and the endless loop all read as
   * one continuous flight through the same sky — the world only ever changes inside the clouds.
   */
  /**
   * BOOKEND VIDEO TRANSITION (2026-07-23, user-supplied clip in /public) — the smoke-burst
   * video used ONLY after the preloader and at the fifth-section restart loop. Composited
   * over the canvas with mix-blend-mode screen (the clip is smoke on black, not transparent);
   * a white veil pulses under its near-white peak so the world swap is truly invisible.
   * The checkpoints keep the custom spiral shader (`cameraFog`) untouched.
   */
  videoTransition: {
    enabled: true,
    src: '/Preloader_Transitiosn&after_fifth_section.mp4',
    /** Clip length (measured: 2.68s @ 25fps, 1920x1080). */
    durationSec: 2.68,
    /** The measured near-white peak (avg luma ≈ 205/255 at ~0.6-0.7s) — the swap moment.
     *  In CLIP time; real time scales by 1/playbackRate. */
    coveredAtSec: 0.72,
    /** Slowed playback (2026-07-23, user: "play the video a little slow") — 0.75× stretches
     *  the 2.68s burst to ~3.6s of screen time, peak landing at ~0.96s. */
    playbackRate: 0.75,
  },

  cameraFog: {
    enabled: true,
    /** FRAME-DRIVEN bookends (cover at frame 0 / clear by 30 / re-cover 322→336) — OFF now
     *  that the bookends are the user's VIDEO transition (2026-07-23); the shader smoke still
     *  serves checkpoint jumps via its forced envelop/release mode. */
    frameDriven: false,
    /** Warm cloud-white, matched to the scene fog family. */
    color: '#f4efe6',
    /** Noise frequency of the smoke — higher = finer curls. */
    scale: 2.6,
    /** Spiral strength: how hard the field rotates toward the screen centre. */
    swirl: 2.6,
    /** Animation speed multiplier for the whole smoke motion. */
    speed: 1.0,
    /** Intro emergence: fully covered until `clearStartFrame`, clear by `clearEndFrame`. */
    clearStartFrame: 6,
    clearEndFrame: 30,
    /** Outro envelope: starts closing in at `coverStartFrame`, total by `coverEndFrame`.
     *  Tightened to the 320 restart grab (2026-07-23 v2, user: "quick and natural, not a
     *  long wait") — the smoke rises as soon as the outro glide begins. */
    coverStartFrame: 322,
    coverEndFrame: 336,
    /** Exponential ease rate for coverage — sized so a full close/part lands in ~1.5-2s
     *  (the user's "only 2 sec" budget). Softens teleports into a breath, never a pop. */
    easeRate: 3.0,
    /** Faster close-in rate for CHECKPOINT jumps (the user just clicked — don't dawdle);
     *  the part-out afterwards runs at the normal `easeRate`. Covered in ~1s at 4.5. */
    jumpEaseRate: 4.5,
  },

  /** Height (altitude) fog — fades the geometry into the cloud sea so the platform
   *  base + lower stage stones DISSOLVE into the same soft cloud haze (the reference
   *  "floating on clouds" look) instead of cutting out hard. See {@link HeightFog}. */
  heightFog: {
    enabled: false,
    /** The cloud-haze tone the base dissolves into (match the sunlit cloud colour). */
    color: '#f3ece1',
    /** Full haze at/below this world-Y (around/below the platform base). */
    bottom: -2.5,
    /** Fully clear above this world-Y (keep the arch + doors crisp). */
    top: 1.8,
    /** Max haze strength (0..1). */
    strength: 1.0,
  },

  /** Simple 4-stop vertical gradient sky (a large BackSide sphere, no atmospheric
   *  simulation) — replaces the physically-based `THREE.Sky` (turbidity/rayleigh/mie/
   *  sun-disc) that used to sit here: at low sun elevations it could blow the whole
   *  frame out to solid white (bloom amplifying the physically-huge sun radiance), and
   *  the user asked to drop it for this flatter, fully art-directed gradient instead
   *  (2026-07-05). `top`/`middle`/`horizon` blend above the horizon line, `horizon`/
   *  `bottom` blend below it. Colours carried over from the last-tuned gradient values. */
  sky: {
    // Cooler, softer grey-blue gradient (2026-07-13, user-tuned in the GUI, set as default).
    top: '#9bbad8',
    middle: '#94adc9',
    horizon: '#ced3d9',
    bottom: '#cfcfcf',
  },

  // NOTE: a custom `Plaster_stone.png` texture for the arch voussoirs (toggleable against the
  // bake) was added and REMOVED the same day (2026-07-17, user: "custom doesn't look good …
  // keep baked only"). The arch stones wear their `Baked_1_S_1` atlas, full stop.

  /** Vegetation BILLBOARD PLANES (2026-07-17) — the `Bush_*`/`Cypress_Tree_*`/`Plant_*`/
   *  `Tree_*` meshes, each a flat plane wearing one cut-out foliage image from
   *  `public/textures/` (matched by mesh name in `orvaeScene.ts`). */
  planes: {
    /** Alpha cutoff. Pixels below this are DISCARDED, so the planes stay depth-writing and
     *  never need transparency sorting (which is what makes overlapping foliage flicker).
     *  Raise if you see halo fringing around the leaves; lower if edges look chewed. */
    alphaTest: 0.5,
    /**
     * Live UV transform per foliage texture — "scaling and move on all direction … to get
     * perfect" (2026-07-17). Tunable from the "Vegetation Planes" GUI folder.
     *
     * NOTE these are per TEXTURE, not per mesh: all six `Cypress_Tree_*_S_3` planes share one
     * material, so tuning `cypress` moves/scales the image on all six together. (Per-mesh
     * tuning would need a material clone per plane — say the word if you want that.)
     *
     * `repeat` is the SCALE: >1 shrinks the image (fits more in), <1 zooms in. `offset` slides
     * it. `rotationDeg` spins about the texture centre. `flipY` flips it vertically — glTF
     * bakes use flipY=false, so start there and toggle if the foliage is upside-down.
     *
     * Values below are the user's own GUI-tuned settings (2026-07-17 screenshots), set as
     * defaults. Bush was pinned at move -1/-1 and Cypress at move X 1 — i.e. both were jammed
     * against the old ±1 slider limits, so the GUI ranges were widened (see `Debug.buildPlanes`)
     * and these are starting points to refine, not necessarily final.
     */
    transform: {
      bush: { offsetX: -1, offsetY: -1, repeatX: 3, repeatY: 3, rotationDeg: 1, flipY: false },
      // ALL SIX cypress trees share the sunlit image now (2026-07-22 — the 4/5/6 "without
      // sunlight" variant and its `cypressShade` transform were removed at the user's request).
      cypress: { offsetX: 1, offsetY: 0.27, repeatX: 3.02, repeatY: 1.56, rotationDeg: 1, flipY: false },
      plant: { offsetX: 0, offsetY: 0, repeatX: 1, repeatY: 1, rotationDeg: 0, flipY: false },
      tree: { offsetX: 0, offsetY: 0, repeatX: 1, repeatY: 1, rotationDeg: 0, flipY: false },
    } as Record<
      string,
      {
        offsetX: number;
        offsetY: number;
        repeatX: number;
        repeatY: number;
        rotationDeg: number;
        flipY: boolean;
      }
    >,
    /**
     * PER-MESH placement (2026-07-22, user: "each plant had that custom position … rotation
     * only on y axis"). Keyed by the mesh's RAW GLB name (e.g. `Cypress_Tree_4_S_3`). Values
     * are DELTAS from the authored Blender transform — {0,0,0,0} (or a missing entry) means
     * "exactly where Blender put it", so a fresh export never fights stale absolutes here.
     * Position offsets are in the mesh's parent space; rotations are per-axis deltas in
     * degrees (X/Z tilt added 2026-07-23 at the user's request, alongside the Y spin).
     * Tuned live from the "Vegetation Planes" GUI folder; the per-mesh "log values" button
     * prints a paste-ready line for this table.
     */
    // Locked 2026-07-23 — FINAL values from the user's plants_settings.png screenshot.
    placement: {
      Plant_1_S_3: { x: 0.72, y: -2.01, z: 0.72, rotXDeg: 0, rotYDeg: 0, rotZDeg: -19.5 },
      Plant_2_S_3: { x: 0.18, y: -2.01, z: 1.27, rotXDeg: 0, rotYDeg: 9.5, rotZDeg: 42.5 },
      Plant_3_S_3: { x: 0.18, y: -2.01, z: 0, rotXDeg: -6.5, rotYDeg: 0, rotZDeg: -42.5 },
    } as Record<
      string,
      { x: number; y: number; z: number; rotXDeg: number; rotYDeg: number; rotZDeg: number }
    >,
  },

  // NOTE: a polished-pillar test material (polished_pillar.png on the four Pillar_* meshes,
  // with grade/roughness/metalness controls) was tried and REMOVED the same day (2026-07-23,
  // user: "baked looks fine, let's keep baked only"). The pillars wear their baked atlases.

  /** The `Bright_Light_White_S_*` portal lights (2026-07-17) — a bright emissive core +
   *  Fresnel rim + an additive halo shell. There's no bloom pass in this pipeline (see
   *  `postprocessing`), so the halo is what makes the light actually spill past the mesh.
   *  See {@link PortalLightMaterial}. */
  portalLight: {
    enabled: true,
    /** Is the light mesh VISIBLE? OFF for the current version (2026-07-21, user: "that portal
     *  light — disable it for the current version"). The whole system stays built; flip this or
     *  the "show light mesh" GUI toggle to bring it back. */
    visible: false,
    /** Core colour. White = a neutral blown-out light source. */
    color: '#ffffff',
    /** Core brightness. >1 pushes it past white for a hotter, more "light source" read.
     *  1.7 → 1.15 (2026-07-19): the core is ADDITIVE now, so the old value read far hotter. */
    intensity: 1.15,
    /** Radial falloff power for the core, centre → edge. Higher = a tighter, smaller hot spot
     *  that fades sooner; ~1 = a broad, soft wash across the whole quad. This is what stops the
     *  portal rendering as a flat rectangle (2026-07-19, user: "see that mesh it's effecting
     *  things, please make it nice"). */
    falloff: 2.0,
    /** Overall opacity of the additive core, on top of the radial falloff. */
    opacity: 0.85,
    /** Slow breathing pulse: amount (0 = steady) and speed (rad/sec). Shared with the halo. */
    pulse: 0.07,
    pulseSpeed: 1.3,
    /** Fresnel rim on the core surface: falloff tightness + how much it brightens the edge. */
    fresnelPower: 2.2,
    rimStrength: 1.1,
    /** The additive glow shell around the mesh — this is the visible "glow". */
    halo: {
      enabled: true,
      /** How much bigger than the source mesh the shell is (scaled about the geometry's
       *  bbox centre, NOT the node origin — positions are baked into the vertices). */
      scale: 1.12,
      color: '#dceeff',
      intensity: 0.85,
      /** Rim falloff (higher = tighter, more of a thin edge glow). */
      power: 2.4,
    },
  },

  /**
   * Interactive water (port of nhtoby311/WaterSurface `WaterSurfaceComplex`, built on
   * three.js `Water2`). Applied to the GLB's EXISTING `Water_Mesh` geometry. Two
   * scrolling normal maps + a real-time planar reflection/refraction, plus a hover FX
   * (ripple sprites and/or a GPU fluid sim) that distorts and tints the surface.
   * Defaults mirror the reference's `WaterSurfaceComplex` props.
   */
  /**
   * CUSTOM TEXTURES (Scene 1 Pillars, Stones, and Doors)
   */
  customTextures: {
    enabled: true,
    pillar: { repeatX: 4, repeatY: 4 },
    stone: {
      repeatX: 6,
      repeatY: 6,
      hue: 0.05,
      saturation: 0.6,
      lightness: 0.5
    },
    door: { repeatX: 1, repeatY: 1 }
  },

  /**
   * CUSTOM SCENE-1 MATERIALS (2026-07-20, user's annotated reference frames + hero image):
   * a SECOND look for the first scene only — real PBR materials with a directional sun and
   * shadows matching the reference (warm light from the RIGHT, shadows falling left across the
   * stones) — kept ALONGSIDE the baked atlases. The "enabled" checkbox swaps the two live;
   * baked stays the shipped default.
   *
   * Mesh families (named per the user's reference annotations):
   *  - numbered voussoirs 1-35        → terracotta brick (varied warm tones, like the hero)
   *  - Door_Main_Pillar / Pillar_* /
   *    Stone_Pillar / Stage_Pillar    → cream travertine trim
   *  - Stage_Stone                    → warm beige stage blocks ("Stage stones")
   *  - Left_Door / Right_Door         → wooden door panels
   *  - Medieval Double Door 9.*       → dark iron hardware
   */
  customScene1: {
    enabled: false,
    /** Sun matching the hero reference: warm, from the right and above. Azimuth is degrees
     *  around Y (0 = +Z toward the camera side, 90 = from +X/right); elevation is degrees up. */
    sun: {
      azimuthDeg: 62,
      elevationDeg: 38,
      color: '#ffd9b0',
      intensity: 2.4,
      /** Soft ambient fill so the shadow side stays readable (hemisphere sky/ground). */
      fillSky: '#bcd3ef',
      fillGround: '#caa07a',
      fillIntensity: 0.55,
      shadows: true,
      shadowMapSize: 2048,
    },
    /** Family base colours (each numbered stone also gets a per-stone tone jitter). */
    colors: {
      brick: '#c2703f',
      brickJitter: 0.12,
      trim: '#e8dcc0',
      stage: '#d9c9a8',
      wood: '#8a5a2e',
      iron: '#3a3632',
    },
    roughness: { brick: 0.88, trim: 0.82, stage: 0.85, wood: 0.7, iron: 0.55 },
  },

  water: {
    enabled: true,
    /** Surface tint multiplied into the REFLECTION (sky/architecture mirrored in). Near-white
     *  so the reflected sky reads crisp and bright against the near-black body — the moody,
     *  strongly-reflective look of the user's reference water (2026-07-13). */
    color: '#e8eef2',
    /** Deep-water body colour seen looking straight DOWN (low reflectance). Set to BLACK to
     *  match the reference's `waterColor #000000` — the water reads dark/deep and lets the
     *  Fresnel reflection dominate at grazing angles. */
    deepColor: '#000000',
    /** Normal-map tiling across the surface. */
    scale: 11,
    /** Flow direction of the two normal maps. */
    flowDirection: [1.0, 0.5] as [number, number],
    /** Flow speed. LOCKED 0.085 (2026-07-22, user default). */
    flowSpeed: 0.085,
    /** Reflection render-target resolution. 512 → 1024 (2026-07-19): with the distortion this
     *  low the mirror is nearly sharp, so the reflection's own resolution becomes visible —
     *  512 read blurry where the reference is crisp. (Adaptive tiers still drop it live.) */
    dimensions: 1024,
    /**
     * Index of refraction. Real water ≈ 1.333. This DRIVES the Fresnel reflectance at
     * normal incidence via Schlick's F0 = ((ior-1)/(ior+1))² (≈0.02 for water), which the
     * shader ramps toward a near-mirror at grazing angles. Higher IOR = more reflective
     * head-on. Replaces the hand-set `reflectivity` as the physical control (2026-07-11).
     */
    ior: 1.333,
    /** Kept in sync with `ior`/`mirror` at runtime (F0) — the actual uniform the shader reads. */
    reflectivity: 0.02,
    /**
     * ARTISTIC MIRROR FLOOR (2026-07-19, user's reference: "it reflects all the mesh…it's like a
     * more mirror…and the reflection has to be very much straight"). Physical water F0 is only
     * 0.02 — almost nothing reflects looking down — but the reference pool reads as a dark
     * mirror from every angle. This floors the base reflectance (`reflectivity =
     * max(F0(ior), mirror)`); Fresnel still ramps it to a full mirror at grazing angles.
     */
    mirror: 0.6,
    /** How strongly the ripple normals distort the mirrored reflection (choppiness read).
     *  0.08 → 0.015 (2026-07-19): the reference's reflection is STRAIGHT — the arch mirrors as
     *  clean vertical lines with only a gentle shimmer, so the normals must barely bend it. */
    distortion: 0.015,
    /** A fake sun/sky GLINT on the water (independent of scene lights, which are off) —
     *  a sharp specular sparkle that sells "wet" without a real directional sun. */
    sunColor: '#fff4dc',
    sunDirection: [-0.3, 0.6, -0.4] as Vec3,
    /** Highlight tightness (higher = smaller/sharper glint) and brightness. */
    sunShininess: 260,
    sunIntensity: 0.6,
    /** Cubemap environment map — drives PBR reflections (and optionally the backdrop).
     *  The water itself mirrors the live scene via a planar Reflector. */
    envMap: {
      enabled: true,
      /** Folder of 6 faces: right/left/top/bot/front/back .png */
      path: '/cubemap/sunset/',
      /** Replace the gradient Sky background with the cubemap. */
      asBackground: false,
      /** scene.environment intensity hint (applied to materials' envMapIntensity). */
      intensity: 1.0,
    },
  },

  /** "Endless" cloud sea the platform floats on — camera-facing billboard sprite puffs
   *  (the mrdoob https://mrdoob.com/lab/javascript/webgl/clouds/ technique: plane
   *  geometry + a soft cloud texture, NOT a volumetric raymarch). Replaces TWO earlier
   *  raymarch attempts (2026-07-02: first a full IQ "Clouds" port, then a cut-down
   *  fBM version) — the user specifically asked for the plane-sprite technique instead
   *  ("don't try that method, that is not working"). Each puff is a `THREE.Sprite`
   *  (auto-billboards to the camera every frame — the "planes rotate with camera"
   *  behaviour, free from three.js) sampling the user-supplied cloud photo/render
   *  (`public/textures/Cloud_Texture.png`, loaded as the `cloud-puff` asset — 2026-07-04,
   *  replaced the earlier procedural canvas stand-in). The whole field recentres onto
   *  the camera's XZ every frame so the viewer can never reach an edge = "endless";
   *  puffs also drift, wrap, and fade/haze toward the sky's horizon colour as they
   *  recede (the "camera LOD" depth cue). See {@link CloudSea}. */
  cloudSea: {
    enabled: true,
    /** Number of puff sprites (2026-07-17, user: "i want more clouds … add more clouds counts").
     *  Raised 420 → 900 — but see `maxPuffs`, which is what actually decides the outcome. */
    count: 900,
    /**
     * Hard cap on spawned puffs. Each puff is its OWN draw call (that's the cost of the
     * billboard-sprite technique), so this — not `count` — is the real ceiling.
     *
     * ⚠️ This was a hard-coded `MAX_PUFFS = 300` inside `CloudSea` while `count` sat at 420:
     * the config asked for 420 and silently got 300 for weeks, and the GUI's 300-500 range did
     * nothing. Now it's a real setting, raised to 1200 so `count` can actually breathe. If the
     * frame rate drops after adding clouds, THIS is the number to pull back.
     */
    maxPuffs: 1200,
    /** Horizontal radius the puffs are scattered within (recentres on the camera). */
    radius: 180,
    /** Puffs only spawn OUTSIDE this radius from centre (which is wherever the camera
     *  currently is, not a fixed point — the field recentres every frame, so this
     *  clear zone follows the camera through all 4 scenes) — keeps whatever's
     *  immediately around the viewer from getting buried by a puff spawning right
     *  underneath it. Pulled in from 20→14 (2026-07-04, user: wanted the clouds
     *  closer/denser — 20 left an oversized gap visible from directly above). */
    innerRadius: 14,
    /** Soft fade width (world units) just OUTSIDE `innerRadius` — a puff that drifts
     *  back through the clear zone fades out over this distance instead of popping,
     *  so "clear near the camera" (2026-07-04, user) holds up as puffs move, not just
     *  at spawn. */
    innerFade: 12,
    /** World-space vertical band the puffs occupy. Sized to sit under the water line
     *  and wrap the platform's base (GLB bounds: water ≈ y0.75, structure bottom ≈ y-7.5). */
    bandBottom: -9,
    bandTop: -1,
    /** Puff size range (world units — a sprite is roughly square). Bigger + more
     *  overlap than the original pass so the sea reads as one continuous mass rather
     *  than separate cotton balls, matching the reference photo — but not SO big that
     *  a single puff can dwarf the platform (see `innerRadius`). */
    minScale: 12,
    maxScale: 24,
    /** Opacity range per puff — adds natural density variation. */
    minOpacity: 0.6,
    maxOpacity: 0.95,
    /** GLOBAL Y lift for the whole cloud/fog sea — "set for all sections my fog Y upper"
     *  (2026-07-23, user). Added to every chapter; tune live from the Clouds GUI. */
    baseY: 0,
    /** PER-CHAPTER Y offset on top of `baseY` (2026-07-23, user: "differing Y value of clouds
     *  per scene") — keyed by chapter index 0-4; the sea EASES to the new height as each
     *  chapter begins (see `CloudSea.setSectionY`).
     *  LOCKED IN 2026-09-04 from the user's tuned GUI values (screenshot). */
    sectionY: { 0: 0, 1: 2, 2: 12, 3: 9, 4: 7.5 } as Record<number, number>,
    /** Drift direction (X/Z used; Y ignored) — wraps within `radius`, so it's seamless. */
    windDir: [1, 0, 0.3] as Vec3,
    /** Drift speed (world units/sec). */
    speed: 0.6,
    /** Sunlit tint (top of the band) vs. shadowed tint (bottom of the band) — each puff
     *  is lerped between these by height. Tuned to sit naturally against `sky.top`/
     *  `sky.horizon` (2026-07-04, user: "use this color of my sky as reference") rather
     *  than an independent cloud palette; the horizon colour is ALSO what distant
     *  puffs haze toward (see `CloudSea.update`). */
    lightColor: '#fef9f0',
    darkTint: '#5c86a3',
    /** Overall opacity multiplier. */
    opacity: 1.0,
    /**
     * SOFT PARTICLES + noise dissolve (2026-07-17, user: the puffs looked "so buggy" where
     * they collide with the models — they want "noise and fog" as clouds pass through the
     * scene). A sprite is a flat quad, so it intersects geometry along a hard straight line.
     * With this on, each puff reads the solid scene's depth ({@link SceneDepth}) and fades out
     * as it nears a surface, with animated fBM fraying the contact so it looks like vapour.
     *
     * COST: one extra depth-only render of the scene per frame, at `resolutionScale` of the
     * canvas, using a trivial override material (see {@link SceneDepth} for what it skips and
     * why). Turn `enabled` off to reclaim that on a weak GPU — the clouds fall back to the
     * old hard-edged intersection.
     */
    /**
     * SCROLL-DRIVEN RISE (2026-07-17, user: "when i scroll those clouds get more up on y
     * position from second scene … clouds gets upper and upper as per i scroll").
     *
     * The whole puff field lifts along Y as you scroll, so the sea creeps up around the
     * architecture the further in you go. Keyed to CLIP FRAMES (not raw scroll %) so it lines
     * up with `SceneSections` and with the left TIMELINE readout — scrub, read the frame off
     * that panel, and put the number here.
     *
     * `startFrame` defaults to Scene 2's boundary (62) so nothing moves during Scene 1;
     * `endFrame` is the clip's end (370), where the lift reaches `height`. Eased with
     * smoothstep, so it creeps in and settles rather than ramping linearly.
     */
    /**
     * CLOUD VERSION 2 — sunlit look (2026-07-19, user's reference image: "the sunlight
     * absorbing … it looks like a cartoonish but also very realistic — let's try it", as an
     * A/B toggle next to the classic look, NOT a replacement).
     *
     * Same sprites, same texture, same placement — v2 only adds per-fragment SUN LIGHTING in
     * the shader: a fake spherical normal is derived from each quad's UV, lit by `sunDir` with
     * soft wrap-around, blending `shadeColor` (the blue-grey crevices between puffs) toward
     * `sunColor` (the warm lit tops). That's what gives the reference its dense sun-absorbing
     * cumulus read. Toggling is a pure uniform flip — instant, no rebuild.
     */
    v2: {
      /** OFF by default — version 1 (the classic sprite look) stays the shipped look. */
      enabled: false,
      /** World-space sun direction — the reference is lit warm from the right. */
      sunDir: [0.65, 0.35, -0.2] as Vec3,
      /** Lit-side colour (warm sunlight soaking the puff tops). */
      sunColor: '#fff0da',
      /** Shadow-side colour (the cool blue-grey the reference shows between the puffs). */
      shadeColor: '#a9bbd3',
      /** Shaping power for the light falloff — higher = harder, more cartoonish light. */
      power: 1.7,
    },

    // NOTE: the scroll-rise feature (sea climbing along Y with scroll) was REMOVED entirely
    // at the user's request (2026-07-23) — per-chapter heights (`baseY`/`sectionY`) replaced it.
    soft: {
      enabled: true,
      /** Depth-pass resolution vs the canvas. 0.5 = half res, which is plenty — the fade is
       *  soft by definition, so a crisp depth buffer buys nothing. */
      resolutionScale: 0.5,
      /** World-space distance over which a puff fades into the geometry behind it. Bigger =
       *  softer, foggier contact. Tuned against the puffs' 12-24 unit size. */
      softness: 6.0,
      /** Frequency of the fBM that frays the contact zone (higher = finer wisps). */
      noiseScale: 3.0,
      /** How hard the noise eats into the fade (0 = a clean ramp, 1 = very ragged). */
      noiseAmount: 0.8,
      /** Drift speed of the noise field — slow, so it churns rather than flickers. */
      noiseSpeed: 0.05,
    },
  },

  /** Volumetric "god rays" / light shafts — a STANDALONE box volume (not sourced from
   *  the GLB; earlier exports had a `God_Rays_Mesh` cube for this, but that's a fragile
   *  dependency on Blender naming, so the box is now built + placed entirely from this
   *  config). A custom additive shader draws animated, noise-broken light shafts
   *  travelling along `rayDir`, in a warm yellow-gold, so it reads as sun rays streaming
   *  through. Depth-tested (the doors/arch occlude it) but writes no depth. Position/
   *  rotation/scale are all live-tunable from the debug GUI. See {@link GodRays}. */
  godRays: {
    // OFF again (2026-07-17, user: "for now off that god rays"). Kept ON briefly on 07-16.
    // Flip to true to bring the light-shaft volumes back.
    enabled: false,
    /** Warm yellow-gold sun-ray colour. Shared across every instance below — tweaking
     *  it in the debug GUI updates all of them at once (2026-07-06, user request:
     *  multiple god-ray volumes that stay visually consistent with each other). */
    color: '#ffcf6b',
    /** Overall brightness of the shafts (additive). Shared. */
    intensity: 1.5,
    /** Shaft frequency across the beam (higher = thinner, more numerous rays). Shared. */
    density: 2.4,
    /** How fast the shafts shimmer/drift. Shared. */
    speed: 0.18,
    /** Soft core glow added on top of the streaks (0..1). Shared. */
    glow: 0.45,
    /** World direction the rays travel along (normalized in-shader). Down + slightly in. Shared. */
    rayDir: [-0.35, -1.0, -0.25] as Vec3,
    /** Edge softness — fades the shafts near the mesh's silhouette so it isn't boxy. Shared. */
    softness: 1.3,
    /** One standalone box volume per entry — independent position/rotation/scale, but
     *  all sharing the appearance settings above. Instance 0 is the original (roughly
     *  the Scene 1 door gap); instance 1 defaults near `Pillar_S_2.002`'s world
     *  position (Scene 2) — live-tunable, the user adjusts placement from the GUI. */
    instances: [
      {
        position: [0, 4, -1] as Vec3,
        rotationDeg: [0, 0, 0] as Vec3,
        scale: [3, 6, 3] as Vec3,
      },
      {
        position: [3.31, 29.19, -109.42] as Vec3,
        rotationDeg: [0, 0, 0] as Vec3,
        scale: [3, 6, 3] as Vec3,
      },
    ],
  },

  /**
   * The ORVAE emblem — a mystical AMBER ENERGY ORB (2026-07-13, user). The `Orave_emble_S_3`
   * sphere wears a custom {@link OrbMaterial} shader: a polished glass shell (Fresnel edge
   * lighting, HDR-env reflection + refraction) wrapping a RAYMARCHED volumetric interior of
   * flowing amber energy — animated 3D simplex noise → FBM → curl-noise advection → domain
   * warping, with Beer-Lambert volume absorption so the energy reads as suspended inside the
   * glass, not painted on. All tunable live in the "Emblem Orb" GUI folder.
   */
  orb: {
    /** Deep/core amber and the hot bright amber the densest energy glows toward. */
    amberDeep: '#7a2a00',
    amberHot: '#ffb24d',
    /** Tint the refracted background picks up passing through the amber body. */
    amberTint: '#ff7a1a',
    /** Volumetric energy: overall density, the threshold that shapes wisps out of the noise,
     *  and Beer-Lambert absorption (higher = denser/darker core, more depth). */
    density: 1.7,
    densityBias: 0.42,
    absorption: 2.6,
    /** Noise field: spatial scale, flow speed (slow + hypnotic), and domain-warp amount. */
    noiseScale: 1.9,
    flowSpeed: 0.14,
    warp: 0.85,
    /** Glass shell: Fresnel edge falloff power + strength, index of refraction, and how much
     *  HDR environment reflection shows at grazing angles. */
    fresnelPower: 3.0,
    fresnelStrength: 1.0,
    ior: 1.48,
    reflectivity: 0.9,
    /** Emissive pop (drives bloom) and the warm rim glow on the Fresnel edge. */
    emissive: 0.85,
    edgeGlow: 0.6,
    /** Raymarch step count through the sphere (quality vs cost). Lowered to 20 + HARD-CAPPED at
     *  48 in `OrbMaterial` (2026-07-13, user: orb was laggy, must run on software/weak WebGL).
     *  20 is smooth; raise only on a strong GPU. */
    steps: 20,
  },

  /** Placeholder PBR palette (until baked textures are provided). */
  palette: {
    stoneOutside: '#c88665', // dusty cream-orange terracotta
    stoneInside: '#c88665', // same for all arch stones
    keystone: '#efe6cf',
    pillar: '#efe6cf',
    platform: '#e8ddc5',
    wood: '#6f4a2c',
    water: '#a0bdd4',
    /** ONE consistent stone tone for ALL arch voussoirs (inner + outer): a richer,
     *  more saturated burnt-orange terracotta — matches the reference photo's brick
     *  wall (2026-07-05; the previous `#d8c3a0` read too pale/washed-out next to it). */
    archStone: '#c2703f',
  },

  lights: {
    // The architecture is fully BAKED (unlit), so scene lights now only fill the custom
    // grass + water. The directional SUN is off (2026-07-11, user: "remove that sun and
    // all") — grass/water read from the ambient + hemisphere + the sky environment map,
    // a soft even fill with no harsh key or shadows.
    ambient: {
      color: '#ffffff',
      intensity: 0.85, // raised to carry the grass now that the sun is gone
    },
    sun: {
      color: '#ffe2bd', // warm golden-hour key
      intensity: 0, // OFF — baked lighting; re-raise if you want a live key back
      /** Upper-front-right, lower elevation → longer, defined cast shadows the front
       *  camera can see (kept moderate so it doesn't blow out under bloom). */
      position: [-35, 20, -15] as Vec3,
      castShadow: false,
      shadow: {
        mapSize: 2048,
        near: 0.5,
        far: 90,
        bounds: 24,
        bias: -0.0003,
        normalBias: 0.025,
        radius: 4,
      },
    },
    hemisphere: {
      enabled: true,
      skyColor: '#dfeaf5',
      groundColor: '#efe3cf',
      intensity: 0.85, // main soft key for grass/water now the sun is off
    },
  },

  scroll: {
    // More scroll distance = the baked animation scrubs SLOWER per scroll (2026-07-13, user:
    // "too fast on scroll, make it slower"). 8 viewport-heights to play the whole 370-frame
    // clip (was 4), plus a gentler wheel response. Both live-tunable in the debug GUI.
    // 8 → 10 pages and 0.45 → 0.34 wheel (2026-09-01, user: "the scrolling is not smooth, it's
    // so fast — I want it slow, animating at the exact frame the user is at"): more scroll
    // distance per clip frame + a gentler wheel response = the journey plays slower and the
    // camera tracks the user's actual position tightly.
    pages: 10,
    lerp: 0.1,
    smoothWheel: true,
    wheelMultiplier: 0.34, // 0.7 → 0.45 (2026-07-21) → 0.34 (2026-09-01), both: "make it slow"
    touchMultiplier: 0.65,
    syncTouch: false,
    /** Reaching the end snaps back to the top so the baked camera path (one
     *  continuous journey through all 4 scenes) replays — an endless loop instead
     *  of getting stuck at the last frame. */
    loop: true,
  },

  interaction: {
    enabled: true,
    recursive: true,
  },

  /**
   * Interactive stones — igloo.inc-style wave. Hovering a stone bulges it (and the
   * adjacent stones, by proximity falloff) OUTWARD along a fixed per-stone direction,
   * driven by a damped spring toward a target that is 0 whenever the cursor is away —
   * so every stone ALWAYS glides back to its exact original position. No collision
   * solver (the outward fan can't drive stones into the structure), so nothing ever
   * gets trapped off-home.
   */
  stones: {
    enabled: true,
    /** Influence radius around the cursor's 3D point — the hovered stone + its
     *  immediate neighbours bulge; everything else stays home. */
    radius: 1.8, // LOCKED 2026-07-21 screenshot
    /** Peak outward displacement (world units) of the stone right under the cursor. */
    maxPush: 0.6,
    /** Falloff sharpness across the radius (higher = tighter wave crest). */
    falloffPower: 2.5,
    /** Damped-spring stiffness toward the target bulge (user-tuned 2026-07-18 screenshot). */
    stiffness: 95,
    /** Spring damping (user-tuned 2026-07-18 screenshot; ≈ critical at 2·√95≈19.5). */
    damping: 26.5,
    /** Hard clamp on displacement (safety). */
    maxOffset: 0.85,
    /** Subtle tumble as a stone bulges. */
    rotation: 0.16,
    /** Below this displacement with the cursor away, snap to exact home. */
    settleEps: 0.0015,
    /**
     * Adjacency-coupled wave (replaces the old fixed inner/outer `pairs`). The real
     * contact graph of all 35 stones is auto-built from their world bounding boxes, then
     * the cursor bulge is DIFFUSED across it so every stone's neighbours rise together
     * with it — they clear out of the way BEFORE a moving stone reaches them, so no two
     * adjacent stones (top, corner, or inner↔outer) ever interpenetrate.
     */
    wave: {
      /** Fraction of a neighbour's bulge an adjacent stone inherits each diffusion pass
       *  (→1 = neighbours move almost identically; lower = tighter, more local wave). */
      coupling: 0.8, // LOCKED 2026-07-21 screenshot
      /** Diffusion passes = how many stone-rings the bulge spreads out from the cursor. */
      passes: 2, // LOCKED 2026-07-21 screenshot
      /** Bounding boxes are inflated by this fraction before the overlap test, to bridge
       *  the mortar gaps so genuinely touching stones register as adjacent. */
      adjacencyInflate: 0.22,
      /**
       * Outer→inner CASCADE. Each stone eases its bulge in at a rate set by its radial
       * layer: OUTER stones use `leadRate` (fast → they move out FIRST, opening a gap),
       * INNER stones use `lagRate` (slow → they follow after a beat, into the cleared
       * space). Set both equal to disable the cascade (all move together).
       */
      leadRate: 24,
      lagRate: 6,
    },
    /**
     * Inner↔outer adjacency pairs (by mesh name) from the `Spring_Stones_adjacent`
     * reference. A hovered stone and its paired neighbour move by ONE shared vector, so
     * the inner voussoir never slides into the outer one. Stones not listed here (the
     * flat facade + pillar blocks: 1, 2, 3, 22–27) move on their own. Edit freely — if a
     * pair looks wrong in-scene, fix the two numbers here. Empty = auto-detect by geometry.
     */
    pairs: [
      ['4', '5'], ['6', '7'], ['8', '9'],
      ['10', '11'], ['12', '13'], ['14', '15'], ['16', '17'],
      ['18', '19'], ['20', '21'],
      ['28', '29'], ['30', '31'], ['32', '33'], ['34', '35'],
    ] as [string, string][],
    /**
     * Stones whose base (`N`) and emissive twin (`N_E`) meshes were named the wrong way
     * round in Blender. At load we swap the two names back, so `N` is the moving stone and
     * `N_E` is the glow twin again. (Stone 2 was exported reversed.)
     */
    swapEmissive: [] as string[], // fixed in Blender (2/2_E) — no swap needed anymore
    /**
     * Rapier rigid-body physics for the arch voussoirs. Each inner/outer stone is a
     * gravity-free dynamic convex body; the pillars/doors/base are static colliders.
     * Hover drives a spring toward the wave target, so stones bulge out, COLLIDE with
     * the pillars (and each other), then are sprung back so the arch always re-forms.
     */
    physics: {
      enabled: true,
      /** Stones collide ONLY with the structure (pillars/base), never each other — so the
       *  packed arch never self-explodes. The hull is shrunk this much around its centre
       *  so it's penetration-free at rest yet still hits a pillar when pushed into it. */
      colliderShrink: 0.78, // committed value — inscribed-sphere collider vs the STRUCTURE only
      /** Collider density → mass; forces are mass-scaled so motion is size-independent. */
      density: 1,
      /** Built-in body damping (on top of the spring) for stability. */
      linearDamping: 0.6,
      angularDamping: 0.85,
      /** Linear spring (acceleration units) pulling each stone toward its wave target. */
      springStiffness: 95,
      springDamping: 15,
      /** Angular spring returning each stone to its rest orientation. */
      angStiffness: 45,
      angDamping: 9,
    },
    /**
     * Per-stone emission: each arch stone `N` has a co-located twin `N_E` that glows
     * while the stone is displaced and fades back to its texture at rest. Only the
     * `_E` twins glow — the base `N` meshes keep their material untouched.
     */
    /**
     * CURSOR-FOLLOW GLOW PLANE (2026-07-20) — the "fake it with a plane" trick, separate from
     * the emission system: a small additive quad with a soft radial glow texture glides along
     * the stone surface under the cursor and fades out entirely off the stones. See
     * {@link CursorGlow}. All live from the "Stone Physics" GUI cluster's sibling folder.
     */
    cursorGlow: {
      enabled: true,
      /** LOCKED 0.8 (2026-07-21, user screenshot: "LOCK THIS SETTINGS") — with the stencil
       *  mask confining it to the emission silhouette, a strong value reads right. */
      opacity: 0.8,
      /** Quad size in world units (the visible spread is ~half this — the texture's falloff).
       *  LOCKED 6 (2026-07-21 screenshot) — big, so the feather sweeps whole stones. */
      size: 6,
      /** WHITE — matches `stones.emission.color`, because this plane exists to smooth that
       *  emission's edges: the two must read as ONE light, not two tints (2026-07-20). */
      color: '#ffffff',
      /** Lift off the surface TOWARD THE CAMERA (world units) — avoids z-fighting on any face. */
      offset: 0.12,
      /** Tie visibility to the hovered stone's LIVE emission intensity: no emission ⇒ no glow,
       *  so the plane only ever smooths the emission's edges and never brightens the resting
       *  baked texture. OFF = the glow appears on hover regardless. */
      emissionCoupled: true,
      /** Seconds for the glide (position/normal lag) and the fade in/out. */
      followSec: 0.06,
      fadeSec: 0.18,
    },

    emission: {
      // ON (2026-07-13, user: "that _E emission mesh is not working, apply that"). The `N_E`
      // arch meshes are DISTINCT geometry (the main body of each voussoir) and MUST keep their
      // baked `Baked_1_S_1` texture — so the hover light-up is now a SEPARATE additive glow
      // OVERLAY added as a CHILD of each `_E` mesh (`StoneInteraction.setEmission`), NOT a
      // material swap. The overlay is invisible at rest and fades in with the stone's
      // displacement, so the twin stays textured AND lights up on hover.
      enabled: true,
      /** WHITE glow on the stone surface (the igloo-gap look). */
      color: '#ffffff',
      /** Base colour of the `N_E` stone bodies themselves. The user wants them rendered as
       *  plain bright WHITE (2026-07-13) instead of their baked texture — so each `_E` mesh
       *  wears a flat unlit material of this colour (three.js default white). Set to null to
       *  keep the baked `Baked_1_S_1` texture instead. */
      twinColor: '#ffffff' as string | null,
      /** Peak emissive on the SURFACE — HDR-bright so it clears the bloom threshold
       *  (the lit architecture stays below it, so only the hovered glow blooms). */
      intensity: 5, // LOCKED (2026-07-21 screenshot; was 1.8)
      /** Displacement (world units) below which the twin stays dark. */
      threshold: 0.04,
      /** Ramp-up rate as the stone moves (higher = snappier light-up). */
      attack: 16,
      /** Fade-out rate as the stone settles (lower = longer afterglow). */
      release: 5,
      /**
       * Pool of point lights that ride the brightest glowing stones so the emission
       * actually lights EVERY surrounding mesh (doors, pillars, base, platform) and
       * casts real shadows from a few of them — strong light SPREAD without a
       * blown-out surface. The bloom pass then halos the bright cores.
       */
      light: {
        // OFF 2026-07-11: the neighbouring meshes are unlit BAKED materials, which ignore
        // real-time point lights entirely — so the spill pool did nothing but cost draw
        // time. The glow itself (additive overlay + bloom) carries the effect now.
        enabled: false,
        count: 5,
        /** Warm yellow-orange light cast onto the neighbouring meshes + the water. */
        color: '#ffae47',
        /** Gentle warm spill onto the neighbours (kept low to match the subtle glow). */
        intensity: 5,
        /** Reach (world units) — large enough to touch the doors/pillars/base. */
        distance: 15,
        decay: 1.7,
        /** The brightest N lights cast shadows (point-light shadows are costly). */
        shadowCount: 2,
        shadowMapSize: 512,
      },
    },
  },

  /** Exact Blender collections from the GLB (node names). */
  stoneCollections: {
    outside: [
      'Stone_02', 'Stone_03', 'Stone_05', 'Stone_07', 'Stone_09', 'Stone_14', 'Stone_16',
      'Stone_17', 'Stone_18', 'Stone_19', 'Stone_23', 'Stone_25', 'Stone_30', 'Stone_32',
    ],
    inside: [
      'Stone_01', 'Stone_04', 'Stone_06', 'Stone_08', 'Stone_10', 'Stone_11', 'Stone_12',
      'Stone_13', 'Stone_15', 'Stone_20', 'Stone_21', 'Stone_22', 'Stone_24', 'Stone_26',
      'Stone_27', 'Stone_28', 'Stone_29', 'Stone_31',
    ],
    keystones: ['Center_Top_stone', 'Left_Top_Stone', 'Right_Top_stone'],
  },

  /** Scene streaming behaviour. `loadAllAtOnce` (2026-07-11, user request) shows ALL
   *  four scenes' geometry at once and skips BOTH the white-smoke intro flash and the
   *  per-section "pop-in" visibility swap + transition between scenes — everything is
   *  simply built and visible from the first frame; scroll still scrubs the one baked
   *  camera through them and eases the focal length per section, just with no fade. */
  scenes: {
    // BACK TO LOAD-ALL (2026-07-21, user: "let's not do that loading one by one scene — load
    // all scenes by once"). This is the 4th flip on this setting; the progressive-reveal
    // machinery stays intact below it, so flipping back is one boolean.
    // (Previous state, kept for history: progressive reveal ON 2026-07-18 per the cinematic
    // spec — which was itself the 3rd request after two rejections.)
    // ---
    // it EXPLICITLY and specifically ("show every scene one by one … when on scene 2 you can see
    // scene 1, further scenes not visible but previous scenes visible" = cumulative). This is the
    // 3rd request; the earlier two reverts were of DIFFERENT framings — honoured the new spec.
    loadAllAtOnce: true,
    /** `cumulative`: show this scene + every earlier one (previous stay visible behind you, future
     *  hidden) — exactly the user's ask. `exclusive`: only the current scene. */
    mode: 'cumulative' as 'cumulative' | 'exclusive',
    /**
     * MASTER SWITCH for the scroll-triggered magnetic pulls (2026-09-01, user: "I'm not sure
     * it's a good thing to have — let's try to remove it and have normal scrolling; recreate
     * every animation"). OFF = plain scrolling everywhere: the camera animates at exactly the
     * frame the user is at (speed-capped, slow-zoned), and the focal length blends CONTINUOUSLY
     * on the approach to each section boundary (`camera.focalRampFrames`) instead of being
     * handed over by a glide. The RESTART loop magnet (frame 320 → replay) and the checkpoint
     * click glides are NOT pulls and stay active either way. Flip back to true to restore the
     * full magnetic choreography — nothing was removed.
     */
    magneticPulls: false,
    /** MAGNETIC transitions (see `SceneSections.MAGNETIC_POINTS`): at a trigger frame scroll locks
     *  and the camera auto-glides to the destination. `magnetDurationSec` = the glide time;
     *  `magnetFlashAt` = fraction of the glide (0-1) at which the white flash + content swap fires. */
    // RETUNED 2026-07-19 (user: "after the white screen goes i want it to go smoothly and then
    // camera"). The flash is 1.25s total (0.5 in + 0.15 hold + 0.6 out — `SceneTransition.play`).
    // With the old 1.8s glide + flash at 0.5, the white was STILL FADING when the glide ended and
    // scroll unlocked (0.9 + 1.25 = 2.15s > 1.8s) — the user could yank the camera mid-fade,
    // which read as the magnet "not working". Now: flash fires at 0.4 × 2.6 = 1.04s, clears at
    // 2.29s, and the camera keeps its eased deceleration until 2.6s — so the white always clears
    // DURING the drift, never after it.
    // Slower again 2026-07-23 (user: "what i told you that i want slow … that magnetic pull");
    // 3.2 → 5.0 on 2026-09-01 ("slow pulling — I don't want the user to recognize it's
    // pulling"); then settled at 4.2 the same day ("still the pulling is slow — I want normal
    // easing"): the length lives here, the FEEL lives in the balanced tangents in
    // Experience.updateMagnet — an even, unhurried glide, never a crawl.
    magnetDurationSec: 4.2,
    magnetFlashAt: 0.4,
    /** Extra seconds scroll stays locked AFTER the glide lands, so the arrival reads as a
     *  composed camera move — the user can't shove the camera the instant it settles. */
    magnetSettleSec: 0.5,
    /** Master switch for the SCROLL-DRIVEN white flashes — section boundaries and the magnets'
     *  mid-glide flash. OFF (2026-07-21, user: "those flash screens we don't need that").
     *  Checkpoint clicks and the frame-300 restart still flash: those are hard cuts that need
     *  covering, and the user explicitly kept the checkpoint behaviour. */
    transition: false,
  },

  /**
   * Animated instanced grass (pmndrs grass-shader style — https://github.com/pmndrs/
   * examples/tree/main/demos/grass-shader). One shared `MeshStandardMaterial` (patched
   * via `onBeforeCompile` for a per-blade wind sway + a base→tip colour gradient, plus
   * three.js' built-in physically-based SHEEN so blades catch the environment light) is
   * instanced across every custom grass mesh in the GLB (`Grass_S_2`, `Grass_01..09_S_3`,
   * `Stage_Grass_S_4`). Blades are scattered onto the real mesh surface (area-weighted
   * `MeshSurfaceSampler`). All of the below is live-tunable from the debug GUI. See
   * {@link Grass}.
   */
  grass: {
    enabled: true,
    /** Blades per world-unit² of a patch's footprint (higher = denser). The FALLBACK for any
     *  scene without its own `densityByScene` entry (i.e. Scene 3's meadow). User-tuned to 75
     *  in the GUI (2026-07-17 screenshot) and set as the default. */
    density: 75,
    /**
     * Per-scene density, keyed by scene index — an ABSOLUTE value in the same
     * blades-per-unit² units as `density` above, NOT a multiplier.
     *
     * CHANGED 2026-07-17 (user: "i want control … second scene grass count and last scene grass
     * count i more like 300 something"): this used to be a multiplier (`{2: 2, 4: 2}` = ×2 on
     * top of the base), which made "set scene 2 to 300" impossible to express directly. It's now
     * a plain override, so the number here IS the density — matching how `sizeByScene` already
     * works, and letting the GUI expose one slider per scene.
     *
     * A scene with no entry falls back to `density`. Scene 1 has no custom grass.
     * Watch `maxBladesTotal` — big patches at 300 can hit the global budget (console warns).
     */
    densityByScene: { 2: 300, 4: 300 } as Record<number, number>,
    /** Per-scene blade SIZE overrides (world units), keyed by scene index — a missing field
     *  falls back to the global `bladeHeight`/`bladeWidth`. Lets Scene 2 / 3 / 4 grass differ
     *  in height and thickness (2026-07-13, user: distinct height & size per scene). Applied
     *  live via `Grass.refreshTransforms` (no re-scatter). Scene 1 has no custom grass. */
    // User's GUI-tuned per-scene sizes (2026-07-17 screenshot), set as defaults.
    sizeByScene: {
      2: { height: 1.5, width: 0.155 }, // Scene 2 field: tall & lush
      3: { height: 0.45 }, //             Scene 3 meadow: very short
      4: { height: 0.55, width: 0.09 }, // Scene 4 stage grass: short & slim
    } as Record<number, { height?: number; width?: number }>,
    /** SAFETY CAPS so grass can't melt a weak/software-WebGL GPU (2026-07-13, user: everything
     *  must run CPU-side). `maxBladesPerPatch` bounds one patch; `maxBladesTotal` bounds the sum
     *  across ALL patches (enforced in `Grass.addPatch` — later patches are trimmed, then skipped,
     *  once the budget is spent, with a console warning). Raise only on a strong GPU.
     *
     *  ⚠️ 2026-07-17: `maxBladesPerPatch` is the REAL density limit on the two big fields, not
     *  `density`. `Grass_S_2` is ~7,000 unit² and `Stage_Grass_S_4` ~9,500 unit², so they hit
     *  60k blades at density 8.5 / 6.3 respectively — every density above that is identical.
     *  At the current caps the scene scatters ~141k blades (S2 60k + S4 60k + S3 ~21k), just
     *  under `maxBladesTotal`. To genuinely thicken S2/S4, raise BOTH caps (GUI: "Grass →
     *  Blade budget"), and watch the frame rate — this is the setting that can melt a weak GPU. */
    maxBladesPerPatch: 60000,
    maxBladesTotal: 150000,
    /** Show a rough brown sand/dirt ground under the blades (procedural) instead of
     *  hiding the source plane — reads as real earth between the grass (user request). */
    ground: true,
    /** Tiling of the sand ground texture across a patch. */
    groundRepeat: 6,
    /** Base blade height (world units). User-tuned to 1.5 and set as default (2026-07-13). */
    bladeHeight: 1.5,
    /** Base blade width (world units) at the root; tapers to a point at the tip. */
    bladeWidth: 0.15,
    /** ± fraction of random height variation between blades (user default: full 1.0). */
    heightJitter: 1,
    // Colours/sheen/roughness below are the user's GUI-tuned values (2026-07-17 screenshot),
    // set as defaults — a warm dark-brown base into olive green, softer sheen than before.
    /** Dark colour at the blade base (dark brown — reads as shadowed earth at the roots). */
    baseColor: '#331900',
    /** SCENE-2 grass wears its OWN colour pair (2026-07-22, user: "differing colour for the
     *  scene-2 grass — add a control for that"). Defaults to the global pair; tune live from
     *  the Grass GUI. Scenes 3/4 keep the global colours. */
    scene2: {
      baseColor: '#331900',
      tipColor: '#2b4700', // locked 2026-07-23 (user's Grass_update.png screenshot)
    },
    /** Brighter colour at the blade tip (the sunlit look). */
    tipColor: '#437000',
    /** Physically-based sheen tint + strength (three.js MeshStandardMaterial sheen) —
     *  the soft retro-reflective rim that makes real grass shimmer against the light. */
    sheenColor: '#659009',
    sheen: 0.23,
    sheenRoughness: 0.16,
    /** Blade base roughness / metalness for the standard-material shading. */
    roughness: 0.47,
    /** Wind sway amount (world units of tip displacement) and speed (user-tuned defaults). */
    windStrength: 0.485,
    windSpeed: 5,
    /** Wind bias direction in the blade's local sway plane (X = sway, Z = secondary). */
    windDir: [1, 0.35] as [number, number],

    /**
     * GRASS CULLING + DENSITY LOD (2026-08-17, user: "the triangles are too much for the camera —
     * anything near the camera should only render").
     *
     * WHY THIS IS THE OPTIMISATION THAT MATTERS HERE. Measured live at chapter 1: the whole
     * ARCHITECTURAL model — all 188 meshes across all four chapters — is only ~49k triangles,
     * while the grass is **1,490,510** (≈97% of every frame). Worse, every patch was created with
     * `frustumCulled = false`, so all 141k blades drew from every camera position, in every pass,
     * even with the camera facing the other way. That single flag is why the frame was pushing
     * 3.1M triangles.
     *
     * THE FIX, three parts:
     *  1. TILING — each patch is diced into a grid of `tileSize` sub-meshes, so culling is
     *     per-tile instead of per-field. One 84×84 unit meadow as a single mesh is "in view" the
     *     moment any corner of it is; as ~20 tiles, only the handful the camera actually looks at
     *     survive the frustum test.
     *  2. FRUSTUM CULLING — turned back ON, with each tile's bounding sphere padded by
     *     `boundsPadding` so a blade bending in the wind can never clip out at the screen edge
     *     (that padding is what `frustumCulled = false` was really working around).
     *  3. DENSITY LOD — blades come off a `MeshSurfaceSampler` in RANDOM surface order, so simply
     *     lowering an `InstancedMesh`'s `count` thins the field UNIFORMLY rather than lopping off
     *     a region. Density ramps from 100% inside `lodNearDistance` to `minDensity` at
     *     `lodFarDistance` — distant grass reads as texture anyway, so the blades removed there
     *     are ones you cannot resolve.
     */
    culling: {
      enabled: true,
      /** Tile edge length (world units). Smaller = finer culling but more draw calls; ~25 keeps
       *  the whole scene's grass around 50 tiles while still culling most of a field. */
      tileSize: 25,
      /** Extra bounding-sphere radius (world units) covering blade height + full wind bend, so
       *  culling can never pop a bending blade at the edge of frame. */
      boundsPadding: 2.5,
      /** Past this distance to the tile's SURFACE a tile stops drawing entirely. Deliberately
       *  generous — the ground plane stays, so a hard cut here would show as a colour change on
       *  the horizon. Thinning (below) does the real work long before this. */
      maxDistance: 420,
      /** Full authored density within this distance. */
      lodNearDistance: 40,
      /** Thinned all the way to `minDensity` at (and past) this distance. */
      lodFarDistance: 180,
      /** Fraction of a tile's blades still drawn at `lodFarDistance` and beyond. */
      minDensity: 0.12,
      /** Past this distance a tile swaps to the LOW-POLY blade (2 height segments = 4 triangles
       *  instead of 5 segments = 10). The extra segments exist only so a blade curves smoothly
       *  under the wind bend; that curve is sub-pixel this far out, so the triangles buy nothing.
       *  Geometry is per-tile, so this is a plain `mesh.geometry` swap — the instance matrices,
       *  and therefore the scatter, are untouched. */
      simplifyDistance: 90,
      /** How often tiles are re-evaluated (seconds) — the camera can't move far in 100ms. */
      updateIntervalSec: 0.1,
      /** Density multiplier applied while a water pool renders its REFLECTION pass. That nested
       *  render redraws the whole scene into a small (128–1024px) target, where a quarter of the
       *  blades is indistinguishable — but at full density it was re-drawing all 1.49M grass
       *  triangles per reflecting pool, per frame. */
      reflectionDensity: 0.25,
    },
  },

  // NOTE: the procedural low-poly conifers ("Christmas trees") that used to live here were
  // REMOVED entirely at the user's request (2026-07-17) — `world/Trees.ts`, `settings.trees`,
  // the World wiring and the GUI folder are all gone. The scene's greenery is now the animated
  // `grass` above + the baked/billboard vegetation planes (`settings.planes`).

  /** Opening cinematic + preloader hand-off (2026-07-13). After the preloader clears, the
   *  baked camera flies itself from `autoplay.startFrame` → `endFrame` (no scroll), then
   *  hands control to scroll (which resumes from `endFrame`, not frame 0). The old igloo
   *  mesh-dissolve reveal (`duration`/`edgeColor`/`noise`) is kept for `SceneTransition`'s
   *  reference but the fly-in is the real intro now. */
  intro: {
    // OFF again (2026-07-19). The shader dissolve was tried and REVERTED the same day: it left
    // the model mesh invisible ("i'm not able to see the mesh"), and the user asked to drop it —
    // "forget that magical animation for that model mesh, keep it simple, camera smoothly starts
    // from 0 frame to 40". The plain camera fly-in in `autoplay` below is the opening move now.
    // Do not re-enable without fixing RevealEffect first.
    enabled: false,
    /** Matched to `autoplay.durationSec` (4.5) minus `delay`, so the dissolve completes exactly
     *  as the camera settles at frame 40 rather than finishing early and sitting static. */
    duration: 4.25,
    delay: 0.25,
    edgeColor: '#ffffff',
    edgeIntensity: 2.0,
    noise: 0.22,
    /** Auto-play camera fly-in — the opening move before scroll takes over. OFF (2026-07-13,
     *  user reverted the cinematic execution — kept the preloader only). Flip to true to
     *  bring back the 0→endFrame opening move + UI reveal. */
    autoplay: {
      // ON (2026-07-18) — user's cinematic flow: after the preloader the camera flies 0→40,
      // stops, and the first section + UI load. Scroll then resumes from frame 40.
      enabled: true,
      /** Clip frames (of the baked camera's `TOTAL_FRAMES` timeline) to fly between. */
      startFrame: 0,
      endFrame: 40,
      /** Seconds the fly-in takes, and a hold at the end before UI + scroll unlock. */
      durationSec: 3.2, // 4.5 → 3.2 (2026-07-21, user: "0-40 animation a little faster")
      holdSec: 0.5,
    },
    /**
     * CINEMATIC BUILD-UP layered over the fly-in (2026-07-20, user: "everything animates like a
     * cinema — clouds animating, clouds revealing, stones appearing, water and everything").
     * Three moves, all safe-by-construction (no transforms — the stones' vertices are world-baked):
     *  - clouds fade up from nothing;
     *  - the arch voussoirs fade in one after another around the arch (per-stone material
     *    clones animate opacity, the shared atlas material is restored after);
     *  - the water starts choppy and SETTLES into the mirror (distortion + mirror tweened).
     * Replays on the frame-300 restart loop, same as the fly-in.
     */
    cinematic: {
      enabled: true,
      /** Clouds: seconds to fade the sea in from 0. */
      cloudsSec: 3.4,
      /** Stones: per-stone fade seconds + stagger between consecutive stones. */
      stoneSec: 0.7,
      stoneStagger: 0.055,
      stoneDelay: 0.5,
      /** Water: seconds for the surface to calm from choppy into the mirror, and how
       *  choppy it starts (multiplier on the authored distortion). */
      waterSec: 4.2,
      waterChop: 8,
    },
  },

  performance: {
    overlay: true,
    reportIntervalMs: 2000,
    adaptive: {
      /** Auto-drop quality (pixel ratio, shadows, water RT, FX) on weak/mobile GPUs. */
      enabled: true,
      minFps: 45,
      recoverFps: 58,
      minPixelRatioScale: 0.6,
      checkIntervalMs: 1000,
      /** STABILITY (2026-07-23, user: "every time it flashes the quality changes"): a tier
       *  moves only after a STREAK of consecutive samples (one bad second can't flip the
       *  whole render config), then holds through a cooldown before being judged again.
       *  Adaptation is also fully suspended during transitions (see PerformanceMonitor). */
      dropSamples: 3,
      raiseSamples: 6,
      cooldownMs: 10000,
    },
    /**
     * QUALITY PROFILES (2026-07-18) — the real cost ladder for weak / software-WebGL ("CPU")
     * devices. Ordered by what actually costs the most per frame in this scene:
     *
     *  1. WATER REFLECTIONS — each in-view pool re-renders the WHOLE scene into its own target.
     *     With 3 pools that's up to 3 extra scene renders/frame. `reflections:false` freezes the
     *     reflection (keeps the last texture) instead — by far the biggest single win.
     *  2. CLOUD PUFFS — every sprite is its own draw call, so the count IS the draw-call budget.
     *  3. SOFT PARTICLES — the cloud depth prepass is another (half-res) scene render.
     *  4. GRASS — blade budget (vertex load).
     *  5. BLOOM resolution.
     *
     * `puffs` and `grassBlades` are only applied at BOOT (changing them later means re-scattering,
     * which hitches); everything else is safe to change live when the FPS monitor shifts tier.
     */
    profiles: {
      // HIGH = the scene exactly as authored. Every optimisation that trades looks for speed is
      // OFF here: full render scale, no mesh culling, the physical (sheen) grass material and the
      // authored grass distances. A capable machine must never be served a reduced experience.
      high: {
        puffs: 900, grassBlades: 150000, reflections: true, waterRes: 1024, softParticles: true,
        bloomScale: 0.5, lodDistanceScale: 1,
        grassDistanceScale: 1, cheapGrass: false, pixelRatioScale: 1, cullPixelSize: 0,
      },
      medium: {
        puffs: 500, grassBlades: 90000, reflections: true, waterRes: 256, softParticles: true,
        bloomScale: 0.35, lodDistanceScale: 0.75,
        grassDistanceScale: 0.6, cheapGrass: false, pixelRatioScale: 0.85, cullPixelSize: 4,
      },
      low: {
        puffs: 220, grassBlades: 40000, reflections: false, waterRes: 128, softParticles: false,
        bloomScale: 0.25, lodDistanceScale: 0.5,
        grassDistanceScale: 0.3, cheapGrass: true, pixelRatioScale: 0.6, cullPixelSize: 10,
      },
    } as Record<
      'high' | 'medium' | 'low',
      {
        puffs: number;
        grassBlades: number;
        reflections: boolean;
        waterRes: number;
        softParticles: boolean;
        bloomScale: number;
        lodDistanceScale: number;
        /** Multiplier on every `grass.culling` distance — thins and culls the field much closer
         *  to the camera on weak devices. */
        grassDistanceScale: number;
        /**
         * Swap the grass off `MeshPhysicalMaterial` + `sheen` onto a plain Lambert material.
         *
         * This is the single biggest FILL-RATE lever in the scene. Sheen is a second full BRDF
         * lobe evaluated per fragment, and the blades are `DoubleSide` covering large areas of
         * screen — on an integrated GPU that shader, not the triangle count, is what costs the
         * frame. The wind sway and the base→tip colour gradient are preserved; only the
         * physically-based shimmer is dropped.
         */
        cheapGrass: boolean;
        /** Render-scale applied at boot for a detected low-power GPU (and on tier changes).
         *  Halving the pixel count is the bluntest and most reliable fill-rate win there is. */
        pixelRatioScale: number;
        /** Written into `settings.visibilityCulling.minPixelSize` — the projected-radius (px)
         *  below which a mesh stops being drawn. **0 = no mesh culling**, which is what the
         *  HIGH tier uses so a good machine renders the scene exactly as authored. */
        cullPixelSize: number;
      }
    >,
  },

  /**
   * SCREEN-SIZE VISIBILITY CULLING (2026-08-17) — stop drawing meshes too small on screen to see.
   * See {@link VisibilityCuller} for the full rationale; the short version is that this scene
   * renders itself 3–5 times per frame (cloud depth prepass, main, bloom, one pass per reflecting
   * water pool), so every far-away mesh costs several draw calls to contribute a smudge. That
   * per-draw overhead is what pins weak integrated GPUs, which is the case this exists for.
   */
  visibilityCulling: {
    enabled: true,
    /**
     * Hide a mesh once its bounding sphere projects to fewer than this many pixels (radius) at
     * the current FOV and viewport height. **0 disables culling entirely.**
     *
     * Written by `Experience.applyQualityProfile` from the active tier's `cullPixelSize`, the
     * same way `settings.lod.distanceScale` is — so this value is the LIVE one and the profiles
     * are where the per-device policy lives. It defaults to 0 because a capable machine should
     * draw the scene exactly as authored (2026-08-18, user: "if someone's PC is good he will get
     * full performance"); only the medium and low tiers turn it on.
     */
    minPixelSize: 0,
    /** Hard distance backstop in world units; 0 = off (the pixel test is the real rule, and a
     *  raw distance cut would pop whole buildings while leaving small props drawing forever). */
    maxDistance: 0,
    /** How often meshes are re-evaluated (seconds). */
    updateIntervalSec: 0.15,
  },

  /**
   * AUTO-LOD / progressive meshes (2026-07-23, user: "like Needle's Auto LODs — triangles are
   * so much, optimize on each scroll"). Heavy meshes get meshoptimizer-simplified index
   * buffers at load; the renderer swaps detail per mesh by camera distance every scroll —
   * full triangles only for what the camera is near. See {@link LodSystem}.
   *
   * ⚠️ 2026-08-17: measured, the ENTIRE model is only ~49k triangles across all four chapters,
   * so this system's whole addressable budget is ~1% of a frame. It is kept (it costs nothing at
   * runtime) but it is NOT where this scene's cost lives — the grass and per-pass draw-call
   * overhead are. See `settings.grass.culling` and `settings.visibilityCulling`.
   */
  lod: {
    enabled: true,
    /** Only meshes with at least this many triangles get variants (small ones aren't worth it). */
    minTris: 3000,
    /** Index-count targets for the two reduced levels (fractions of the original). */
    midRatio: 0.4,
    lowRatio: 0.15,
    /** Camera distances (world units, to the mesh's BOUND) where detail steps down. */
    midDistance: 60,
    lowDistance: 150,
    /** Dead zone (world units) against level flicker at a boundary. */
    hysteresis: 8,
    /** How often the distances are re-evaluated. */
    checkIntervalSec: 0.2,
    /** Tier-coupled multiplier (see performance.profiles.lodDistanceScale) — weak devices
     *  pull the reduced levels closer to the camera. */
    distanceScale: 1,
  },

  /**
   * BLOOM (2026-07-18) — the glow on the emissive stones + orb + portal light. Real
   * `UnrealBloomPass` maths, but composited ADDITIVELY over a direct render instead of running the
   * frame through the composer — because the composer re-tone-maps everything, including the SKY
   * and the baked atlases, which is exactly what the user doesn't want. The sky + water are
   * excluded from the bloom source, and `threshold` > 1.0 means only HDR emitters bloom.
   * See {@link SelectiveBloom}.
   */
  selectiveBloom: {
    /**
     * MASTER SWITCH for the whole emissive-glow look (2026-07-19, user: "make when only i on post
     * processing than only that emission works, instead it will not work").
     *
     * This is the ONE flag — the debug GUI's "postprocessing" toggle drives it, and every glow
     * site keys off it: the bloom pass itself, the bloom-layer opt-in on the portal light + emblem
     * ({@link World.setGlowEnabled}) and on the stones' hover overlays
     * ({@link StoneInteraction.setGlowEnabled}), plus the additive halo shells. OFF ⇒ the emissive
     * objects render as plain unlit meshes with no glow of any kind.
     *
     * ON by default (2026-07-19, user: "post processing enable it by default").
     */
    enabled: true,
    /**
     * GPU GATE (2026-07-18, user: "if user has GPU detected then GPU having that emission and
     * bloom thing; if not, make sure it's white mesh colour and had little glow, not too much").
     *
     * The bloom pass costs a second scene traversal + a blur mip chain every frame — real money on
     * a software/CPU renderer. When true, the whole pass is SWITCHED OFF unless a hardware GPU is
     * detected (`Capabilities.hardwareAccelerated` && !lowPower). The emissive objects still read
     * as lights on that path, for free: the portal core is already an unlit blown-out white
     * material and its additive halo shell + the stones' additive hover glow give a soft glow with
     * ZERO extra passes — `fallbackGlow` just trims their intensity so it stays subtle.
     */
    requireGpu: true,
    /** Halo/glow multiplier used on the no-GPU path ("little glow, not too much"). */
    fallbackGlow: 0.6,
    /** LOCKED 1.55 (2026-07-22, user screenshot: "lock in this settings"). */
    strength: 1.55,
    /** Blur spread. LOCKED 1.3 (2026-07-22 screenshot). */
    radius: 1.3,
    /** Only the emissive objects are drawn into the bloom source at all, so this can stay low —
     *  it just shapes how much of each emitter's own falloff blooms. */
    threshold: 0.0,
    /** Final additive intensity of the glow laid over the frame. Keep ≤1 for a subtle look.
     *  0.75 → 0.4 (2026-07-19, "too strong"). */
    overlay: 0.4,
    /** Bloom render resolution as a fraction of the canvas. 0.5 = half-res: visually free (bloom
     *  is blurry) and ~4× cheaper — this is what keeps it viable on weak / software-WebGL GPUs.
     *  `Capabilities.lowPower` drops it further at boot. */
    resolutionScale: 0.5,
  },

  postprocessing: {
    // OFF again (2026-07-18, user reverted — the full composer re-tonemaps the baked atlases and
    // darkens them). The glow the user wants on the emissive stones + orb is now done by the
    // SEPARATE additive `selectiveBloom` OVERLAY above, which never touches the baked base image.
    enabled: false,
    /** Selective bloom: the RenderPass writes UNTONEMAPPED linear HDR, so the threshold
     *  is in linear space. It must sit ABOVE the brightest lit surface (otherwise the
     *  sunlit terracotta blooms into white blotches) yet below the (now HDR) emissive
     *  stones. threshold 1.2 + gentle strength keeps the architecture calm. */
    bloom: {
      enabled: true,
      strength: 0.32,
      radius: 0.5,
      threshold: 1.2,
    },
  },

  audio: {
    // ON (2026-07-18) — the interactive stone hover/collide SFX below need it. Was a dormant
    // integration seam until now (no assets existed).
    enabled: true,
    muteOnBlur: true,
    masterVolume: 1,
    /**
     * PREMIUM STONE INTERACTION AUDIO (2026-07-18, from the user's detailed brief). The 35 arch
     * voussoirs are split into 5 SOUND FAMILIES by stone number (the user's exact lists). Each
     * family has one handcrafted recording in `public/fx/Stones/`; a stone plays its family's
     * "lift" sound as it floats out and the shared "Stone Return" as it settles home. See
     * {@link StoneAudio} (sample-first, procedural fallback) and `StoneInteraction`.
     *
     * Replaced the earlier inner/outer 2-sound system (the `Inner_stones`/`OuterStones` OGGs are
     * now unused, left on disk). The velocity→loudness + trigger/reset/cooldown values below are
     * carried over from that system (the user tuned + liked them).
     *
     * NOTE `minVolume` (0.88) > `maxVolume` (0.61): the user's intentional inversion (slow moves
     * read louder). Kept as-is.
     */
    stoneFx: {
      enabled: true,

      // --- Families: which stone numbers belong to each, + its recording and base trims. -------
      // `stones` are the arch voussoir numbers (1..35). `volume`/`rate` are per-family base
      // gain / pitch (playback rate), on TOP of the velocity loudness + per-play jitter.
      families: {
        // A — Top cap stones. (rate 0.5 = an octave down / slower = heaviest — user default.)
        A: { stones: [1, 2, 3], sample: '/fx/Stones/Heavy Top Stone.ogg', volume: 0.99, rate: 0.5 },
        // B — Wall blocks.
        B: {
          stones: [4, 5, 8, 9, 10, 13, 14, 17, 18, 21, 24, 27, 28, 31, 32, 35],
          sample: '/fx/Stones/Wall Stone.ogg',
          volume: 1,
          rate: 1,
        },
        // C — Arch stones.
        C: { stones: [6, 7, 11, 12, 15, 16, 19, 20], sample: '/fx/Stones/Arch Stone.ogg', volume: 1, rate: 0.93 },
        // D — Column / decorative trim.
        D: {
          stones: [22, 23, 25, 26, 29, 30, 33, 34],
          sample: '/fx/Stones/Decorative Column Stone.ogg',
          volume: 1,
          rate: 1,
        },
        // E — Keystone (the symbolic centre). ⚠️ `stones` is EMPTY: A-D already cover all 35, and
        // the keystone is one specific number that OVERRIDES its A-D family. Put that number here
        // (e.g. [23]) to make the centre stone play the keystone sound — set it from the GUI/scene.
        E: { stones: [] as number[], sample: '/fx/Stones/Keystone (Special Sound).ogg', volume: 1.1, rate: 1 },
      },
      // Shared "returning home" set-down sound (all families settle with this).
      // `rate` doubles as SPEED (a sample's playback rate = its speed): >1 = faster (user:
      // "this sound i want fast"). `fadeOut` = a smooth exponential ease-off over this many
      // seconds at the END of the return, so it "fades off" gently and keeps ringing for a beat
      // AFTER the stones have reached home — no new sound once they're settled, just this tail.
      return: { sample: '/fx/Stones/Stone Return.ogg', volume: 1, rate: 1.25, fadeOut: 0.32 },

      // --- Per-play micro-variation (brief: stops one recording from sounding like a UI sample). -
      // User-tuned defaults (2026-07-18 screenshot): more pitch spread, delay OFF.
      pitchJitter: 0.08, // ± playback-rate variation per hit (±8%).
      volumeJitter: 0.04, // ± volume variation per hit (±4%).
      delayMinMs: 0, //     random start delay lower / upper bound (ms). 0/0 = no delay (user).
      delayMaxMs: 0,

      // --- Levels ------------------------------------------------------------------------------
      masterGain: 0.6, // 0.85 → 0.6 = ~30% quieter (2026-07-18, user: "make it 30% more lessar").
      settleLevel: 0.5, //  fixed loudness of the gentle "return/settle" sound (not velocity-driven).

      // --- TONE: bass + pitch (2026-07-18, user: "increase the base and make low pitch … i want
      // more base"). A shared EQ on ALL stone voices + a global pitch multiplier, so the whole
      // set gets deeper and heavier without re-balancing each family. See {@link StoneAudio}. ----
      /** Global playback-rate multiplier on top of every family's own `rate`. <1 = deeper +
       *  slower (heavier), 1 = as recorded. */
      pitch: 0.8,
      /** Low-shelf BASS boost: everything below `bassFreq` (Hz) is lifted by `bassGain` (dB).
       *  This adds low-end weight WITHOUT slowing the sample (unlike lowering pitch). */
      bassFreq: 180,
      bassGain: 7,
      /** Gentle high-cut (Hz) to tame the brightness that reads as "loud" — a warmer, calmer
       *  top end. Raise toward 20000 to disable. */
      lowpass: 5500,

      // --- Event detection (per family) — a 3-phase machine: idle →(rise past `triggerLevel`)→
      // LIFT sound → lifted →(fall past `returnLevel`)→ RETURN sound → returned →(fall past
      // `resetLevel`, fully home)→ idle. -----------------------------------------------------
      /** A family's peak stone MOTION (displacement ÷ maxOffset) must rise past this to LIFT. */
      triggerLevel: 0.12,
      /** While lifted, motion falling past this fires the RETURN — set ABOVE `resetLevel` so the
       *  return plays WHILE the stones are still visibly moving back (synced), and its fade tail
       *  rings on as they reach home. */
      returnLevel: 0.08,
      /** Motion must fall below this (essentially home) before the family can LIFT again. */
      resetLevel: 0.03,
      /** Min seconds between two LIFTs of the same family. */
      cooldown: 0.04,

      // --- Velocity → loudness (peak-held cursor speed; user-tuned) -----------------------------
      minSpeed: 1.0,
      maxSpeed: 9.6,
      speedDecay: 12.5,
      minVolume: 0.88, // (intentionally > maxVolume — see note above)
      maxVolume: 0.61,
    },
    /**
     * GLOBAL ambient track (2026-07-18, user: "one global sound … after preloader i want that
     * sound get started"). Loops under the whole scene, fading in once the preloader clears.
     *
     * ⚠️ `src` is EMPTY until the user drops their file in `public/fx/` and sets the name here —
     * the file isn't in the project yet. With `src:''` the whole thing no-ops (no console error).
     *
     * BROWSER AUTOPLAY: audio can't start until the first user gesture (click/scroll/touch).
     * Howler resumes on that, and `Experience` ALSO kicks the ambient on the first pointer/scroll
     * as a fallback — so it starts the instant the user does anything, even if the post-preloader
     * attempt was blocked.
     */
    ambient: {
      // UNIVERSAL SOUND — RE-ADDED 2026-07-18 (user: "new music universal continue playing on
      // loop"). Plays continuously under the whole experience (alongside the per-scene beds),
      // starting after the preloader.
      enabled: true,
      src: '/fx/Scenes_Sepcific_Music_WIth_looping_starting_fade_and_ending_Fade_So_it_will_feel_Smooth/Universal_sound.ogg',
      volume: 0.24,
      loop: true,
      fadeIn: 2,
      /** Playback rate — pitch and speed together, like a record player (1 = as authored).
       *  Live from the GUI (2026-07-19, user: "i want that universal sound in my control —
       *  pitch and speed and bass"). */
      rate: 1.0,
      /** Low-shelf boost/cut in dB below `bassFreq` — the "more bass" knob. 0 = untouched.
       *  Applied via a BiquadFilter spliced into the ambient Howl's node chain
       *  ({@link AudioManager.setBass}). */
      bass: 0,
      bassFreq: 200,
    },

    /**
     * SCENE-SPECIFIC + SPATIAL audio (2026-07-18, user's brief). Each chapter has its own looping
     * music bed (crossfaded on section change); some scenes add ENVIRONMENTAL loops positioned in
     * 3D (water pool, the Orvae emblem) that swell as the camera nears the mesh — Howler spatial
     * (PannerNode) attenuates by camera distance, so a source is only audible when you're near it.
     * A one-shot transition swell plays under the white flash. All files are the user's, in
     * `public/fx/Scenes_Sepcific_Music.../`. See {@link SceneAudio}.
     *
     * Loops are keyed by SECTION (chapter 1-5), not model scene. Every loop OGG is authored with
     * its own fade head/tail so it seams smoothly.
     */
    scenes: {
      enabled: true,
      /** Base folder (the user's long folder name). */
      base: '/fx/Scenes_Sepcific_Music_WIth_looping_starting_fade_and_ending_Fade_So_it_will_feel_Smooth',
      /** Per-chapter music bed level + crossfade time (ms). 0.35→0.455 = +30% (2026-07-19, user
       *  wanted the scene beds louder while water/wind/universal came down). */
      loopVolume: 0.455,
      crossfadeMs: 1400,
      /** Chapter (section 1-5) → its looping bed, relative to `base`. */
      loops: {
        1: '/Scene_01/Scene_01_Loop.ogg',
        2: '/Scene_02/Scene_02_Loop.ogg',
        3: '/Scene_03/Scene_03_Loop.ogg',
        4: '/Scene_04/Scene_04_Loop.ogg',
        5: '/Scene_05/Scene_05_Loop.ogg',
      } as Record<number, string>,
      /**
       * Positioned 3D loops. `mesh` = the GLB node the sound emits from (world position resolved
       * at load); `refDistance`/`maxDistance`/`rolloff` shape the falloff (small ref = tight, only
       * near the mesh). `scenes` limits which chapters it runs in (perf); empty = all.
       */
      // WATER LOOPS REMOVED (2026-07-19, user: "remove that water sound effect, it's so boring").
      // The three per-pool `Water_Loop.ogg` sources that used to live here are gone on purpose —
      // the OGG stays in `public/fx/.../Scene_01/` if they ever want it back. Only the emblem
      // spatial source remains.
      spatial: [
        { key: 'emblem', file: '/Scene_03/Orvae_Emblem_Sound_loop.ogg', mesh: 'Orave_emble_S_3', volume: 0.7, rate: 1.0, refDistance: 4, maxDistance: 40, rolloff: 1.4, scenes: [] as number[] },
      ],
      /** Chapter-1 wind — a broad ambient bed (not a point source), fades with the section.
       *  0.35→0.245 = ~30% quieter (2026-07-18, user). */
      wind: { file: '/Scene_01/Wind_Loop.ogg', volume: 0.172, scenes: [1] as number[] },
      /** The white-flash swell. 16-17s clip: `rate` speeds it up toward the flash length; `volume`
       *  75% per the user; fades in/out so it's never abrupt. */
      transition: { file: '/Trasitions/Transition.ogg', volume: 0.75, rate: 1.0, fadeMs: 500 },
      /**
       * One-shot CUES fired when the camera passes a clip FRAME (scrubbing back re-arms them).
       * `frame` is in the same units as the left TIMELINE readout, so scrub to the moment the
       * doors actually swing and set it exactly. Default is a placeholder in chapter 1.
       */
      cues: [
        { key: 'door', file: '/Scene_01/Open_Door_01.ogg', frame: 52, volume: 0.8, rate: 1.0 },
      ],
    },
  },

  /**
   * CINEMATIC OVERLAY (2026-07-18, user: "in front of camera add noise … cinematic feel" +
   * "add vignette effect on the camera smooth"). A DOM layer OVER the canvas — deliberately not
   * a WebGL post pass, because re-enabling the composer would re-tone-map the baked atlases and
   * wreck the baked look. Film grain (animated noise, `overlay` blend) + a soft edge vignette.
   * See {@link CinematicOverlay}.
   */
  cinematic: {
    grain: {
      enabled: true,
      /** Grain visibility (0 = off). Filmic at low values. */
      opacity: 0.035, // LOCKED (2026-07-22, user)
      /** Noise tile zoom (bigger = coarser grain). */
      scale: 1.4, // LOCKED (2026-07-22, user)
      /** ANIMATE the grain? OFF (2026-07-18, user: "make it fix … it should not moving, fix once
       *  that grain image"). When false the grain is a fixed static overlay. */
      animate: false,
      /** If `animate`, how many times/sec the grain re-scatters. */
      fps: 24,
    },
    /**
     * CIRCULAR RAINBOW LENS RING (2026-07-20, user: "like after rain … a circle rainbow — I
     * want that effect, just very very subtle"). A conic-gradient spectral ring masked to a
     * thin circle, screen-blended and heavily blurred, sitting in the same DOM overlay as the
     * grain — zero GPU cost, every parameter live.
     */
    rainbow: {
      enabled: true,
      /** Overall visibility. Keep LOW — the ask is "very very subtle". */
      opacity: 0.14,
      /** Clip frame past which the ring fades out (2026-09-03, user: "at frame 170 fade out
       *  that rainbow") — Chapter 3 runs to 188, but the ring leaves early. Driven off the
       *  CAMERA's frame each tick; the ring's 2.4s CSS transition carries the fade. */
      fadeOutFrame: 170,
      /** Ring diameter as a fraction of the viewport's smaller side. */
      size: 0.62,
      /** Ring thickness as a fraction of its radius. */
      thickness: 0.16,
      /** Blur in px — high = soft atmospheric haze rather than a crisp arc. */
      blur: 22,
      /** Ring centre in viewport fractions (0.5/0.5 = dead centre). */
      x: 0.5,
      y: 0.46,
    },
    vignette: {
      enabled: true,
      /** Edge darkness (0-1). */
      strength: 0.5,
      /** How far in the darkening reaches (0 = only the very corners, 1 = well into frame). */
      softness: 0.6,
      color: '#000000',
    },
  },

  /**
   * FRONT-END NAVIGATION UI (2026-07-18, from the user's 5 section designs). Top bar (sound +
   * ABOUT + MUSIC), right-rail progress with interactive checkpoints, per-chapter headline/
   * subtitle with GSAP reveals, scroll cue, the final CTA, and the About overlay (camera blur).
   * See {@link UIManager}.
   */
  ui: {
    /** Dev master switch — turn the whole overlay off to work on the model unobstructed
     *  (GUI "UI → visible"). 2026-07-18 user request. */
    visible: true,
    /** Dark scrim behind the text for legibility (user: "backdrop in 1A1E24"). 0 = none.
     *  0.28 → 0.22 (2026-09-01 softening) → 0.12 (2026-09-02, user: "the backdrop is visible,
     *  looks bad"): the per-title radial halo (`.ui-content::before`) now carries the local
     *  contrast, so the global wash is barely a breath. */
    scrimColor: '#1A1E24',
    scrimOpacity: 0.12,
    /** Per-chapter content (index = section 0-4). `align` places the headline block; the
     *  subtitle is always bottom-left. Titles use `\n` for the line break. */
    chapters: [
      { label: 'Chapter 01', title: 'Where Vision\nBecomes Legacy', subtitle: 'Every extraordinary journey begins with a single decision.', align: 'left' },
      { label: 'Chapter 02', title: 'The First\nStep Matters', subtitle: 'We invest before certainty exists when conviction matters most.', align: 'right' },
      { label: 'Chapter 03', title: 'Where Vision\nMeets Endurance', subtitle: 'Patient partnerships designed for decades, not funding rounds.', align: 'center' },
      { label: 'Chapter 04', title: 'Opening Doors\nTo Tomorrow', subtitle: 'The next generation of enduring companies starts with one decision.', align: 'left' },
      { label: 'Chapter 05', title: "Building What's\nNever Been Built", subtitle: 'The next generation of enduring companies starts with one decision.', align: 'right' },
    ] as Array<{ label: string; title: string; subtitle: string; align: 'left' | 'right' | 'center' }>,
    /** The final CTA (chapter 5). */
    /** The final-chapter CTA button. OFF (2026-09-02, user: "for the last section I do not
     *  want that button, the conversation one") — the copy stays here so re-enabling is one
     *  boolean. */
    ctaEnabled: false,
    cta: 'Begin the Conversation',
    /** About overlay copy (redesigned 2026-07-23 from the user's `About_orvae.png` mock:
     *  "ABOUT US" chip top-centre, big white arch logo centre, credits paragraph at the bottom.
     *  `body` is injected as HTML — the credit links are real anchors (hrefs are placeholders
     *  until the user provides the studio/creator URLs). */
    about: {
      title: 'ABOUT US',
      body:
        'ORVAE is an immersive concept exploring the relationship between architecture, innovation, and purposeful investment. ' +
        'Through cinematic storytelling, interactive environments, and thoughtful design, it reimagines how venture capital can be experienced in the digital world. ' +
        'Designed and Developed by <a class="ui-about__link" href="https://undreamstudio.com/" target="_blank" rel="noopener">UNDREAM STUDIO.</a> ' +
        'Scene Creation by <a class="ui-about__link" href="https://www.instagram.com/thecreativeady/" target="_blank" rel="noopener">Atharva Yadav.</a>',
    },
    /** Camera blur (px) applied to the canvas while the About overlay is open —
     *  "so much blur" (2026-07-23 v2), up from 14 → 20 → 28; restored 2026-09-02 after the
     *  brief shader-mode experiment ("I want the blur instead"). The About's own flowing
     *  sky-shader canvas (`ui/AboutSky.ts`) now floats over this blur. */
    aboutBlurPx: 28,
    /**
     * ENTER gate on the preloader (2026-07-18). When loading finishes, hold on an ENTER button
     * instead of auto-starting. Two wins: it uses the user's `Page_loader_Button_onclick` sound,
     * and — importantly — the CLICK unlocks the browser's audio context, so the scene beds /
     * universal loop / spatial audio all start reliably instead of being silently blocked until
     * the first interaction. Set false to auto-start (audio may then be silent until a click).
     */
    enterGate: true,
    enterLabel: 'Enter Orvae World',
  },

  /**
   * STORY MODE (2026-08-18) — the per-chapter "Know More" experience.
   *
   * THE FLOW the user specified, end to end:
   *  1. A quiet uppercase KNOW MORE sits under the chapter headline, its underline sweeping in
   *     from the left on hover and out through the right on leave.
   *  2. One CLICK opens the story — no hold, no ring cursor (2026-08-18 v5: the user dropped
   *     both outright).
   *  3. There is no break, flash or shake. The only transition is black cloud-smoke rolling in
   *     over the scene on a magnetic ease — slow to leave, fast through the middle, slow to
   *     settle — until the world is nearly gone.
   *  4. What remains is narrated by a female voice and subtitled bottom-centre, one word at a
   *     time, each word's letters resolving out of blur.
   *  5. A CLOSE button (same underline sweep) or Escape pulls the smoke back out.
   */
  storyMode: {
    enabled: true,
    /** Chapters (0-based) that get a "Know More" button — the user's "section 1, 2, 3 and 5". */
    chapters: [0, 1, 2, 4] as number[],
    /** Button label — plain uppercase, no icon (user: "more simple, like capital words"). */
    label: 'KNOW MORE',
    /** The story's own close control. */
    closeLabel: 'CLOSE',
    /**
     * OPEN/CLOSE IMPULSE (2026-09-02 v2 — the shake + glitch that briefly accompanied this
     * were removed the same day, user: "just keep the camera thing"). ONE move: the camera
     * DOLLIES BACK `distance` units — fast to begin, long to settle (power3.out over `inSec`)
     * — held for the whole story, then eased home over `outSec` on close.
     */
    recoil: { distance: 1.6, inSec: 2.6, outSec: 1.5 },
    /**
     * CINEMATIC DARK GRADE (2026-08-18, user: "more cinematic, like dark tune, everything looks
     * more something"). Two layers working together:
     *
     *  - IN-SCENE: every baked `MeshBasicMaterial.color` is lerped toward `tint` by `strength`.
     *    Those materials are UNLIT and their `color` multiplies the atlas, so this genuinely
     *    darkens the architecture itself rather than just laying a filter over the top. Each
     *    material's original colour is snapshotted on entry and written back verbatim on exit.
     *  - ON-GLASS: a near-black full-screen wash and the faintest vignette. Deliberately flat:
     *    anything with visible shape here reads as a coloured band across the top and bottom of
     *    the frame, which is what the user objected to.
     */
    grade: {
      /**
       * IN-SCENE, via the Perlin shadow-flow shader ({@link StoryGrade}).
       *
       * v2 (2026-08-18, user: "you're just making it dark — what I want is the shadows FLOWING
       * through each and everything … that perlin transition effect"). v1 lerped every material
       * colour uniformly, which was flat. Now an animated world-space simplex field drives a
       * dissolve, so the dark arrives as smoky fronts creeping across the architecture, and
       * because the field is world-space a shadow crossing a wall continues onto the floor.
       */
      /** Deep shadow colour — where the cloud is thickest. Near-pure black now (2026-09-01,
       *  user: "I want more dark colors, like fully black and white"). */
      tint: '#020203',
      /**
       * SECOND colour — the thin, torn edges of the smoke.
       *
       * A grade toward one flat colour reads as "the lights went out". Two colours read as a
       * WORLD: `tint` sinks the thick cloud to near-black while `rimTint` catches the torn
       * edges. Because the mix is driven by cloud DENSITY rather than by luminance alone, the
       * colour itself moves with the smoke.
       *
       * Repalette 2026-09-01 (user: "fully black and white and orange moving … something for
       * contrast"): the violet edge became a pale SMOKE GREY, so the field reads monochrome
       * black-and-white — the only colour left in the frame is the moving orange emblem
       * energy, which is exactly the contrast asked for.
       */
      // NEON-EDGE REWORK (2026-09-02 v3, from the user's reference screenshot): the pale-grey
      // thin-smoke regions are GONE — in the reference the world's interior is pure black and
      // only edges glow, so the second colour is now just a slightly-lifted charcoal.
      rimTint: '#1e1e24',
      /** How strongly `rimTint` shows in the thin parts of the cloud (0-1). */
      rimAmount: 0.25,
      /** Extra darkening where cloud density peaks — the visible black masses rolling over the
       *  meshes, on top of the flat `darkness` crush. */
      cloudDepth: 0.74,
      /**
       * Multiplier on luminance inside the graded region — the crush.
       *
       * 0.10 (2026-08-18 v4, down from 0.32 — user: "so much darker colours, not even barely
       * something is looking"). The architecture should survive only as silhouette and the
       * faintest edge detail, so the subtitle is the brightest thing on screen by a wide margin.
       */
      darkness: 0.03,
      /** Colour retained inside the grade (0 = fully monochrome, 1 = original saturation).
       *  ZERO since the neon-edge rework — the black world carries no colour of its own; the
       *  orange edges are ALL the colour there is. */
      saturation: 0,
      /** How hard the remaining light is pushed toward `tint` (0-1). 0.8 → 0.6 with the
       *  neon-edge rework: both tint colours are near-black now, so less push is needed. */
      tintAmount: 0.6,
      /** World-space frequency of the noise. Lower = bigger, slower masses of smoke. */
      noiseScale: 0.017,
      /** How fast the field drifts — the "flowing" itself. Slow: this is drifting smoke, not
       *  weather. */
      flowSpeed: 0.16,
      /** Softness of the dissolve front (0 = hard wipe, 1 = very diffuse smoke). High, because
       *  the whole effect now IS the transition — there is no shatter covering the hand-off. */
      edge: 0.8,
      /**
       * DOMAIN WARP — what turns drifting smoke into something LIQUID (2026-08-18 v6, user:
       * "the shader actually moving through those mesh like liquid, more immersive").
       *
       * Instead of sampling the cloud field at the surface position, the position is first
       * displaced by ANOTHER noise field. Straight FBM gives you soft blobs that fade in place;
       * warping the lookup makes the field fold and curl through itself, so the darkness appears
       * to POUR across a surface and stream around its forms. It is the single biggest
       * difference between "fog" and "something alive moving through the scene".
       *
       * 0 = no warp (plain FBM). Above ~1.4 it stops reading as flow and starts to churn.
       */
      warp: 0.85,
      /** Frequency of the warp field relative to the cloud field. Below 1 = long, lazy folds. */
      warpScale: 0.55,
      /**
       * EMBLEM ENERGY (2026-09-01, user: "each mesh has that effect that we have on the orvae
       * emblem with dark colors … more immersive"). The emblem's signature look is a view-angle
       * fresnel rim plus flowing hot filaments; this lays the same language over EVERY graded
       * mesh while a story is open — in the grade's own dark violet palette, so the whole scene
       * reads as one living material rather than architecture with the lights off.
       */
      emblem: {
        /** Silhouette-edge strength for the neon-edge look. 0.9 → 0.5 (2026-09-02 v3): at the
         *  old weight a smooth wall seen at a grazing angle lit up ACROSS ITS WHOLE FACE —
         *  bright cream slabs instead of black shapes with burning outlines. */
        fresnel: 0.5,
        /** Rim tightness — raised 2.8 → 5.5 with the neon-edge rework so the fresnel hugs the
         *  true silhouette instead of washing over grazing faces. */
        fresnelPower: 5.5,
        /**
         * DARK REPALETTE (2026-09-02, user: "for those meshes, instead of orange make it dark
         * color, black"). The rim is now a cold dark silver — barely-lit metal edges in the
         * night — and the filaments (below) became BLACK VEINS: because pure black can't be
         * ADDED, the flame band now blends the surface TOWARD its dark colours instead, so
         * ink-black energy visibly pours across the pale smoke regions. The contrast flipped:
         * light ground, dark flowing energy.
         */
        fresnelColor: '#3a3a42',
        /** Strength of the flowing dark veins riding the cloud crests (0 = off). */
        flow: 0.8,
        /** Vein colour — near-pure ink. */
        flowColor: '#050508',
        /** Second vein shade — a charcoal the veins breathe toward, so the black isn't flat. */
        flowColor2: '#26262e',
      },
      /**
       * Seconds for the smoke to roll fully in. Long on purpose — this IS the transition now,
       * and the user asked for "magnetic pulling", which needs room to accelerate and settle.
       */
      durationSec: 3.4,
      /** Seconds for the grade to retreat on close — quicker than it arrived. */
      outDurationSec: 2.2,
      /** Screen wash laid over the frame — near-black and heavier now that this is the only
       *  darkening besides the in-scene grade. Neutral black since the 2026-09-01 repalette. */
      overlayColor: '#020203',
      overlayOpacity: 0.62,
      /**
       * Vignette strength (0-1). REDESIGNED 2026-09-02 (user: "vignette from top and bottom,
       * not corners — imagine bottom is 100% and by the middle it's 0%, with a little blur"):
       * no longer a radial corner glow but two LINEAR bands — full-strength at the top/bottom
       * edges fading to nothing by mid-frame — each backed by a progressively-masked backdrop
       * blur, so the frame melts softly out of focus toward its edges. See `#story-grade` in
       * style.css; this value is the band's peak darkness.
       * 0.55 → 0.85 (2026-09-02 v3): the reference's top and bottom sink to ABSOLUTE black.
       */
      vignette: 0.85,
      /**
       * ATMOSPHERE (user: "sky changes to dark"). The sky dome and cloud sea are custom
       * `ShaderMaterial`s with no `<map_fragment>` chunk to patch, so they are graded by
       * snapshot/restore of their own colour uniforms instead of the noise dissolve.
       */
      sky: {
        // Sunk further for the neon-edge rework (2026-09-02 v3) — the reference sky is
        // near-absolute black with only a breath of tone at the horizon.
        top: '#040405',
        middle: '#0a0a0d',
        horizon: '#141419',
        bottom: '#040405',
      },
      /** Cloud tint + how far their opacity is pulled down inside the story. Crushed harder
       *  in the neon-edge rework — the reference frame has no visible cloud mass. */
      cloudTint: '#131318',
      cloudOpacity: 0.5,
      /** SCENE-FOG colour inside the story (2026-09-02 v3 — the "distant meshes stay pale" bug:
       *  THREE's distance fog applies AFTER the material grade, so every far surface was being
       *  washed back toward the DAYTIME cream haze `settings.fog.color`. The story lerps the
       *  fog itself to near-black, so distance now sinks things INTO the dark). */
      fogColor: '#060607',
      /**
       * EMISSION LINES (2026-09-02, user: "lines going through the mesh, like emission lines —
       * white-orange lines with bloom, slow flowing"). Travelling iso-bands of the SAME smoke
       * field the grade already computes: thin lines with a white-hot core and an orange halo
       * sweep slowly across every graded surface, like energy tracing the architecture's
       * contours. They ride the dissolve mask, so they arrive with the ripple and never exist
       * outside shader mode.
       */
      /**
       * VOICE PULSE (2026-09-02 balance pass, user: "it doesn't look balanced … I want the
       * user to focus more on the subtitles"). The story world's orange energy — the mesh
       * ember crests AND the sky's flowing lines — breathes WITH the narrator: eased toward
       * `speaking` while she talks, sinking to `idle` embers in her silences. The scene
       * visibly listens to the words, which is what steers the viewer's eye to them.
       */
      pulse: { idle: 0.25, speaking: 1.0 },
      lines: {
        /**
         * NEON-EDGE look (2026-09-02 v3, from the user's reference screenshot: a black world
         * where only the geometry's EDGES glow — theirs violet/cyan, ours ORANGE). `intensity`
         * is the overall edge-glow strength (the voice pulse still breathes it); `edgeGain`
         * amplifies the baked texture's own luminance gradient into the fine interior contour
         * lines (brick seams, stair nosings); the silhouette half comes from the fresnel
         * (`emblem.fresnel`/`fresnelPower`). density/speed/width still shape the SKY's
         * travelling lines only.
         */
        intensity: 1.3,
        edgeGain: 9,
        /** Sky-line band count (the dome's flowing lines). */
        density: 2.5,
        /** Sky-line travel speed. */
        speed: 0.16,
        /** Sky-line thickness. */
        width: 0.1,
        /** Weak-edge colour — deep ember, melting into the black. */
        color: '#5c2406',
        /** Strong-edge colour — burning amber (the reference's neon, in orange). */
        coreColor: '#ffa348',
      },
      /**
       * COLOUR RIPPLE (2026-09-02, user: "when I click KNOW MORE … a ripple effect, the ripple
       * changes the colors"). The grade's dissolve is gated by an expanding world-space sphere
       * centred ON THE VIEWER at the click: the darkness ripples outward from you into the
       * distance as the grade rises (and retracts back toward you on close). `maxRadius` is how
       * far the wave must reach to cover the scene (world units); `feather` is the softness of
       * its travelling front.
       *
       * 300 → 800 (2026-09-02, user: "in section 2 the shade doesn't apply to the last scene —
       * why?"): the camera journey spans ~500 world units, so from a mid-journey chapter the
       * far scenes sit beyond a 300-unit wave — they simply never got reached. 800 (+feather)
       * covers the whole world from ANY chapter's camera; the front just travels a little
       * faster to get there in the same sweep time.
       */
      ripple: { maxRadius: 800, feather: 150 },
      /**
       * FLUID FANTASY SKY (2026-09-02, user: "in shader mode I want the sky more crazy —
       * something fluid moving in the sky, to feel more fantasy"). Domain-warped noise veils
       * drift through the night dome; ridge crests catch a faint ember light so the sky
       * belongs to the same black-and-orange world as the emblem energy below.
       */
      skyFlow: {
        /** Strength of the pale smoke veils (0 = plain gradient night sky). 0.4 → 0.18 in the
         *  neon-edge rework — the reference sky is nearly empty black; the veils are only a
         *  whisper of movement now. */
        intensity: 0.18,
        /** Veil colour — pale charcoal, a breath lighter than the night sky. */
        veilColor: '#34343e',
        /** Strength of the sky's FLOWING LINES (2026-09-02 v2 swap, user: "make it inverse —
         *  the mesh shader on the sky"): the orange-and-black contour lines now drift across
         *  the dome, sharing the `lines` look below; the old ember-crest glow moved down onto
         *  the meshes. (`emberColor` retired in the same swap — both layers colour from
         *  `lines.color`/`lines.coreColor` now, one language everywhere.)
         *  0.9 → 0.45 in the balance pass — the voice pulse carries the drama now. */
        emberAmount: 0.45,
        /** Veil frequency — lower = bigger, slower masses. */
        scale: 2.4,
        /** Drift speed of the whole field. */
        speed: 0.32,
      },
    },
    /**
     * NARRATION — the chapter read aloud by a female voice, subtitled in sync (user: "text to
     * voice, realtime, with subtitle, and that subtitle and voice has to sync").
     *
     * Uses the browser's built-in `speechSynthesis`: no API key, no network round trip, no audio
     * asset to ship. Sync comes from the utterance's own `boundary` events, which report a
     * character index per word — the voice's true position rather than an estimate. See
     * {@link StoryNarrator} for the timed fallback used where boundary events never fire.
     */
    voice: {
      enabled: true,
      /**
       * Preferred voices, best first. Matched as a case-insensitive substring against the
       * installed voice list; first hit wins. These are the common FEMALE en-US/en-GB voices
       * across Windows, macOS, iOS and Chrome.
       */
      preferred: [
        'Google UK English Female',
        'Microsoft Aria',
        'Microsoft Jenny',
        'Microsoft Zira',
        'Samantha',
        'Karen',
        'Moira',
        'Tessa',
        'Fiona',
        'Google US English',
      ] as string[],
      /** Language filter used when none of `preferred` is installed. */
      lang: 'en',
      /**
       * VOICE CHARACTER (2026-09-01, user: "female voice more slim, not too harsh — more
       * immersive, feel like space ambient, more professional"). Within what the Web Speech API
       * allows: a touch slower (unhurried = professional), pitch lifted slightly (thins/softens
       * the timbre), volume eased back so she sits IN the ambience rather than on top of it, and
       * the pad ducks less (see duckMusicTo) so the space never empties around her. True studio
       * processing (reverb/auto-tune) is impossible on speechSynthesis — the browser plays it
       * outside the Web Audio graph; that upgrade needs pre-rendered narration files.
       */
      rate: 0.88,
      pitch: 1.1,
      volume: 0.9,
      /** Duck the music/ambience to this multiplier while the narrator speaks. Raised 0.25 →
       *  0.38 (2026-09-01): the space pad stays present under her, which is what makes the
       *  voice read as "in a space" rather than dry over silence. */
      duckMusicTo: 0.38,
      /** Words-per-minute assumed by the FALLBACK subtitle timer when the browser gives us no
       *  boundary events (Firefox). Only used in that path. */
      fallbackWpm: 155,
    },
    /**
     * SPACE AMBIENCE (2026-08-18 v6, user: "I want space ambient background effect so the woman's
     * voice feels more immersive").
     *
     * Synthesised live with Web Audio rather than shipped as a file: a drone that has to sit
     * under a voice for a variable, unknowable length needs to loop seamlessly and duck on
     * command, and a few oscillators do that perfectly for zero bytes and no loop seam. See
     * {@link StoryAmbience}.
     */
    ambience: {
      enabled: true,
      /** Master level of the pad. Deliberately low: this is the room the voice stands in, not
       *  a piece of music. */
      volume: 0.16,
      /** Root of the drone (Hz). C1-ish — felt more than heard. */
      rootHz: 43.65,
      /** Harmonics stacked over the root, as frequency multiples. A fifth and two octaves give
       *  an open, unresolved chord that never asks for a resolution. */
      partials: [1, 1.5, 2, 3, 4.5] as number[],
      /** Cents of detune between the paired voices — the slow beating that makes it feel wide. */
      detuneCents: 7,
      /** Lowpass cutoff (Hz) and how far the slow LFO sweeps it — the "breathing". */
      cutoffHz: 620,
      sweepHz: 340,
      /** Seconds per breath. Very slow: this should never become a rhythm you can follow. */
      breathSec: 19,
      /** Level of the filtered noise bed layered under the drone — the "air" of the space. */
      airLevel: 0.05,
      /** Fade times (seconds). */
      fadeInSec: 3.5,
      fadeOutSec: 2.2,
      /** The pad drops to this multiple of `volume` while the narrator actually speaks, so her
       *  voice always sits clearly on top of it. */
      duckTo: 0.45,
    },
    /** Seconds after the story opens before the first line arrives — long enough that the
     *  smoke has visibly taken the scene before any words appear. */
    subtitleDelaySec: 1.4,
    /** Shown under the last line while the story settles, before it returns on its own. */
    endPrompt: 'Returning',
    /**
     * Seconds the last line is held after the narration finishes, before story mode closes
     * ITSELF and the scene comes back (2026-08-18 v6, user: "after the voice thing is done you
     * should take off that shader mode and come back to normal").
     *
     * Long enough to read the closing line without feeling hurried; the CLOSE button and Escape
     * still work throughout, so nobody is ever held here.
     */
    autoCloseAfterSec: 4.5,
    /**
     * Per-chapter story copy, keyed by chapter index. `title` heads the panel; `paragraphs` are
     * narrated and subtitled one at a time, bottom-centre.
     */
    stories: {
      0: {
        title: 'Where Vision Becomes Legacy',
        paragraphs: [
          'Every extraordinary journey begins with a single decision. Not a plan. Not a certainty. A decision.',
          'The arch remembers the hands that set each stone. What endures was never the material — it was the intent.',
          'This is where vision stops being a thought, and becomes a place you can stand inside.',
        ],
      },
      1: {
        title: 'The First Step Matters',
        paragraphs: [
          'We invest before certainty exists, when conviction matters most and proof matters least.',
          'The first step is the only one taken in darkness. Every step after it is lit by the first.',
          'That is the step we are built to take with you.',
        ],
      },
      2: {
        title: 'Where Vision Meets Endurance',
        paragraphs: [
          'Patient partnerships, designed for decades — not for funding rounds.',
          'Water shapes stone not by force, but by returning. Again, and again, and again.',
          'We measure our work the way architecture measures itself: in what is still standing long after we are gone.',
        ],
      },
      4: {
        title: 'Building What Has Never Been Built',
        paragraphs: [
          'The next generation of enduring companies starts with one decision.',
          'Every doorway here opens onto something that did not exist until someone insisted that it should.',
          'Build what has never been built. We will hold the light while you do.',
        ],
      },
    } as Record<number, { title: string; paragraphs: string[] }>,
  },

  world: {
    placeholder: {
      enabled: true,
      groundSize: 40,
    },
    model: {
      autoCenter: false,
      normalizeScale: false,
      targetSize: 6,
      castShadow: true,
      receiveShadow: true,
      frameOnLoad: true,
      /**
       * AUTO SMOOTH (2026-09-01, user: "all my mesh I want auto smooth — I forgot to do that in
       * Blender"). Re-averages every vertex normal across co-located vertices at load, keeping
       * edges sharper than `angleDeg` hard — the code-side equivalent of Blender's Shade Auto
       * Smooth, so no re-export is needed. In-place and index-preserving (LOD-safe); see
       * `utils/autoSmooth.ts`. Re-exporting from Blender with auto smooth remains the cleaner
       * source-of-truth fix, at which point this becomes a harmless no-op.
       */
      autoSmooth: { enabled: true, angleDeg: 30 },
    },
  },

  debug: {
    /** Show the lil-gui control panel + perf HUD without needing the `?debug` URL flag.
     *  Set false (or use `?debug`) to hide them for a clean production view.
     *  OFF for the public Cloudflare deploy (2026-09-07) — `?debug` still works. */
    forceEnabled: false,
    /** Left-side TIMELINE readout (scroll % + CLIP FRAME + section). Independent of the
     *  lil-gui panel — it's how scene/animation boundary frames get pinned down
     *  (`SceneSections.SECTIONS[].startFrame`). Set false to hide. 2026-07-17.
     *  OFF for the public deploy (2026-09-07): unlike the GUI it has no `?debug`
     *  gate, so it was painting over the live site. Flip true while authoring frames. */
    scrollReadout: false,
    helpers: {
      axes: false,
      grid: false,
      sunHelper: false,
      shadowCameraHelper: false,
    },
  },
};

export type Settings = typeof settings;
